import { parseFieldKey, recordLeaseKey } from '@colplat/protocol';
import type { FieldValue, PresenceUser, ServerMessage } from '@colplat/protocol';
import type { OutboxDocumentWrite } from '@colplat/core-client';
import {
  applyButtonReadOnly, applyFieldReadOnly, applySelectReadOnly, askConfirm,
  dateFieldHtml, escapeHtml, formSnapshot, formString, currencyChipHtml, formatAmount, makeDraggable, mountDateField,
  notchedFieldHtml, presenceDotHtml, stepForFraction, todayIso, wireSwipeNav,
} from '@colplat/ui-web';
import { inlineAccountIcon } from './webledgerAccountDisplay.js';
import {
  attachmentBarHtml, mountAttachmentBar,
} from './webledgerAttachments.js';
import {
  emptyTagSelection, loadTags, loadTagSelection, tagSelectionMembers,
  renderTagSelection, wireTagSelection,
  type TagDto,
} from './webledgerTags.js';
import { openAccountPicker } from './webledgerAccountPicker.js';
import { accountLabelHtml, accountShortLabel } from './webledgerAccountTree.js';
import { apiFetch, failureMessage } from '../api.js';
import { appHref } from '../globals.js';
import { ageLabel, knownOffline, readCached } from '../offlineReads.js';
import { datasetLedger } from './webledgerLedger.js';
import { attachmentsOf, normalTransferKind, standingBefore, transferAmounts } from '@colplat/app-webledger/domain';
import { debtHelperHtml } from './webledgerDebtHelper.js';
import { RecordHold, holdNoticeHtml, type Holder, type HoldView } from './webledgerHold.js';
import { forgetForm, guardUnsaved, rememberForm, takeSavedForm } from './webledgerFormRestore.js';
import {
  splitNumbering, tagLinkTargets,
  type HistoryContext, type TransactionHistory,
} from './webledgerHistory.js';
import { creditLabel, debitLabel } from './webledgerLabels.js';
import { PREF } from './webledgerPrefs.js';
import {
  documentToEdit, heldDifferences, heldFor, queuedPayloadToDto, queuedUpdateFor,
  type AccountDto, type CurrencyDto, type HeldDifference, type QueuedTransactionPayload,
  type SplitDto, type TransactionDto, planOfflineDelete,
} from './webledgerOfflineState.js';
import { createHistoryPanel } from './webledgerHistoryPanel.js';
import { clearPageNotice, showPageNotice, type WebledgerClient } from '../webledgerClient.js';

/**
 * The transaction editor: one modal, shared by every page that shows
 * transactions.
 *
 * It lives in its own module because it is not a property of the register.
 * Editing a transaction means claiming its record lease, joining its
 * presence, streaming previews to other viewers, honouring a take-over,
 * holding an interrupted draft, queueing offline, and offering the change
 * timeline — and a second implementation of any one of those would be a
 * second set of bugs in the part of the app where bugs cost the most. The
 * reports page opens *this* editor rather than one of its own, which is also
 * why the module keeps no page state: everything it needs arrives as an
 * argument.
 *
 * A host page owes it three things: `transactionEditorModalHtml()` somewhere
 * in its shell, a `<div id="wlTimeline">` for the history slide-over, and one
 * call to `wireSplitHelp(root)` after render.
 */


// Inline SVGs, not an icon-font class (`bi bi-*`) — this app never loads
// Bootstrap Icons (see packages/ui-web/src/accountIcons.ts's own doc comment
// on why: no external icon-font dependency, monochrome currentColor SVGs
// instead). Colour is never the only signal for balanced/unbalanced
// (invariant #11) — legacy's own balance indicator lacks an icon entirely;
// we add one deliberately since our own invariant requires it.
const CHECK_ICON = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" style="vertical-align:-2px"><path d="M2 8.5l3.5 3.5L14 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const WARNING_ICON = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" style="vertical-align:-2px"><path d="M8 1.5l7 12.5H1z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M8 6v3.2M8 11.3v.1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
const HELP_ICON = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><circle cx="8" cy="8" r="6.75" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M6.1 6.05a1.95 1.95 0 1 1 2.55 1.86c-.42.14-.65.5-.65.94v.4" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M8 11.6v.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

/**
 * The split-entry explanation. Lives behind the header's help button rather
 * than always-on under the table: as a permanent paragraph it took more
 * vertical room than the split table itself on a phone, for something you
 * need once while learning the form and never again.
 */
const SPLIT_HELP_TEXT = 'Enter each split as an increase or a decrease for that account (labelled per its own type, '
  + 'e.g. "Increase"/"Decrease" for assets). When a split\'s account uses a different currency, its '
  + '"Acct-currency amount" records what actually moved in that account — this is what sets the exchange rate '
  + 'for that leg, exactly as it was recorded originally.';

/**
 * The transactions/register page — matching legacy's `reports.php`. Editing
 * a transaction is a document GROUP (ADR-0021): the whole record
 * (transaction + every split) drafts, validates and commits atomically under
 * one lease claimed on the transaction itself, not live per-field editing.
 */

export interface BootstrapModalLike { show(): void; hide(): void }
export function bsModal(el: Element): BootstrapModalLike | undefined {
  return (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance(e: Element): BootstrapModalLike } } })
    .bootstrap?.Modal.getOrCreateInstance(el);
}

import { timeAgo } from './webledgerTimeAgo.js';

export { timeAgo };

/** The editor modal's markup. Include it once per host page. */
export function transactionEditorModalHtml(): string {
  return `
    <div class="modal fade" id="txModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header py-2">
            <span class="cf-drag-grip" aria-hidden="true">⠿</span>
            <!-- Stepping through the list this was opened from. Beside the
                 title because that is what they change, and hidden outright
                 when the caller has no list to step through — an inert pair
                 of arrows is a worse answer than none.

                 The arrows point where the marker goes, not where time
                 goes: the log is drawn newest first, so the PREVIOUS
                 transaction — the older one — is further down it, and the
                 next is further up. Chevrons left and right said neither,
                 and on a vertical list they invited the reading that the
                 pair moved between two columns. -->
            <span class="btn-group btn-group-sm me-1" id="txNav" hidden role="group"
              aria-label="Move through the list">
              <button type="button" class="btn btn-outline-secondary" id="txPrevBtn"
                aria-label="Previous transaction — older, further down the list"
                title="Previous — older, down the list">&darr;</button>
              <button type="button" class="btn btn-outline-secondary" id="txNextBtn"
                aria-label="Next transaction — younger, further up the list"
                title="Next — younger, up the list">&uarr;</button>
            </span>
            <div class="fw-semibold" id="txModalTitle">Transaction</div>
            <div class="d-flex align-items-center gap-2 ms-auto">
              <button type="button" class="btn btn-sm btn-outline-secondary" id="txSimpleBtn" hidden>Open the simple editor</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" id="txHistoryBtn" hidden>History</button>
              <span class="cf-help">
                <button type="button" class="cf-help__btn" id="txHelpBtn"
                  aria-label="How splits work" aria-expanded="false" aria-controls="txHelpPanel">${HELP_ICON}</button>
                <span class="cf-help__panel" id="txHelpPanel" role="note">${SPLIT_HELP_TEXT}</span>
              </span>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
          </div>
          <form id="txForm">
            <div class="modal-body">
              <div class="alert alert-warning py-2 small mb-3" id="txHeldBanner" role="status" hidden></div>
              <div class="alert alert-secondary py-2 small mb-3" id="txReadOnlyNotice" hidden></div>
              <!-- Notched, like the split table below and like the short
                   form: the label belongs in the control's own border. A row
                   where some fields are named above and some are not is a
                   staircase, and this row sits directly above a table that
                   already does it the other way. -->
              <div class="row g-2 mb-3">
                <div class="col-md-3">${notchedFieldHtml('Date',
                  dateFieldHtml('txDateInput', 'Date', { name: 'txDate', required: true }))}</div>
                <div class="col-md-2">${notchedFieldHtml('Reference',
                  '<input class="wl-nf__ctl" name="reference" aria-label="Reference">')}</div>
                <div class="col-md-4">${notchedFieldHtml('Description',
                  '<input class="wl-nf__ctl" name="description" aria-label="Description">')}</div>
                <!-- The currency the entry balances in. Not a question in the
                     ordinary case: with every leg in one currency it can only
                     be that one, and a new entry takes it from the money
                     account (autoTxCurrency). The picker appears when the legs
                     are in more than one currency — the one case where which
                     of them the debit and credit columns are counted in is a
                     real choice. -->
                <div class="col-md-3">${notchedFieldHtml('Balanced in',
                  '<span class="wl-nf__ctl" id="txCurrencyFixed"></span>'
                  + '<select class="wl-nf__ctl" name="currencyCode" id="txCurrencySelect" required'
                  + ' aria-label="Currency the entry balances in"></select>')}</div>
              </div>
              <!-- Held-draft markers for the four header fields. Under the row
                   rather than under each control: Reference is two columns
                   wide, and a strip carrying a name, a value and a button
                   inside it wrapped to four lines and spilled over its
                   neighbour. Each marker names its field instead. -->
              <div id="txHeldFields"></div>
              <div class="cf-table-scroll">
                <table class="table table-sm cf-table wl-splits">
                  <thead><tr>
                    <th>Account</th>
                    <th class="text-end">+ <span class="txCurLabel text-secondary"></span></th>
                    <th class="text-end">&minus; <span class="txCurLabel text-secondary"></span></th>
                    <th>Acct-currency amount</th><th>Wallet</th><th>Memo</th><th></th>
                  </tr></thead>
                  <tbody id="txSplitRows"></tbody>
                  <tfoot><tr>
                    <td class="text-end text-secondary">Totals</td>
                    <td class="text-end fw-semibold" id="txTotDebit"></td>
                    <td class="text-end fw-semibold" id="txTotCredit"></td>
                    <td colspan="3" id="txBalance"></td>
                  </tr></tfoot>
                </table>
              </div>
              <button class="btn btn-sm btn-outline-secondary" type="button" id="txAddSplit">Add split</button>
              <!-- Why money crossed between two wallets (ADR-0023).
                   
                   Shown once any leg's money is in a wallet. It used to wait
                   for the legs to cross two wallets, but "Maintenance paid
                   this for the car" is one leg in one wallet and still a debt
                   between two (ADR-0023, amendment of 2026-10-18). An entry
                   with no wallet anywhere still does not get the question.
                   
                   The short form has had this since wallets arrived; without
                   it here, expressing a loan in the full editor meant either
                   a pair of loan-tracking accounts or two transactions with
                   nothing joining them. -->
              <!-- A LIST, because one entry can carry more than one of these.
                   Four legs and a single "from/to" pair cannot say which pair
                   of legs the loan was about, and the read path had to guess:
                   its guess matched twice and counted the loan twice. Each row
                   names its own leg, so there is nothing left to work out. -->
              <div class="wl-xfer mt-3" id="txMove" hidden>
                <span class="cf-h">Between wallets</span>
                <div id="txMoveRows"></div>
                <div class="d-flex align-items-baseline gap-2 flex-wrap">
                  <button type="button" class="btn btn-sm btn-outline-secondary" id="txMoveAdd">
                    Add a loan or hand-over</button>
                  <span class="small text-secondary" id="txMoveHint">Money crossing wallets is
                    just a move unless one of these says otherwise.</span>
                </div>
              </div>
              <!-- Tags. Hidden until the transaction exists, for the same
                   reason as files: a tag needs a transaction to be on. -->
              <div class="wl-tag-bar mt-3" id="txTags" hidden>
                <span class="cf-h">Tags</span>
                <span id="txTagList"></span>
                <select class="form-select form-select-sm w-auto" id="txTagPick" aria-label="Add a tag">
                  <option value="">Add a tag…</option>
                </select>
                <span class="text-secondary small" id="txTagNote" role="status"></span>
              </div>
              ${attachmentBarHtml('tx')}
              <!-- Merging two bank entries into one. Hidden unless this
                   transaction actually came from a bank feed — see
                   probeMergeable(). -->
              <div class="cf-card p-2 mt-3" id="txMergePanel" hidden></div>
            </div>
            <div class="modal-footer py-2">
              <!-- Failures land here rather than at the top of the body: the
                   body scrolls, and on a transaction with a dozen splits a
                   banner above the date fields is off-screen at the moment
                   Save is pressed. The footer is always in view, and the
                   message is beside the button that produced it.

                   Not a live region: client.notify announces these, and a
                   second announcing element would either read every message
                   twice or, with aria-live, swallow the announcements whole
                   -- bootstrapWebledgerClient binds notify to the first
                   [aria-live] in the page. -->
              <div class="alert alert-danger py-2 small mb-2 w-100" id="txError" hidden></div>
              <span class="text-secondary small" id="txReadOnlyFooterNote" hidden>Read-only — this updates live while it stays open.</span>
              <button class="btn btn-outline-danger btn-sm me-auto" type="button" id="txDeleteBtn" hidden>Delete</button>
              <button class="btn btn-outline-secondary btn-sm" type="button" id="txMergeBtn" hidden>Merge with the other half</button>
              <button class="btn btn-outline-secondary btn-sm" type="button" id="txCancelBtn" data-bs-dismiss="modal">Cancel</button>
              <button class="btn btn-primary btn-sm" type="submit" id="txSaveBtn">Save</button>
            </div>
          </form>
        </div>
      </div>
    </div>`;
}

/**
 * Rows follow SPEC §6.1's field vocabulary rather than inventing a list-only
 * one: the border carries the state and a chip notched into it carries the
 * identity. Colour is never the only signal — every chip has a glyph and a
 * word beside the dot (invariant #11).
 */
/**
 * Tap/click and keyboard handling for the header's help popover. Hover is
 * pure CSS (pointer devices only — see `.cf-help`), so this covers touch and
 * keyboard: toggle on activation, dismiss on an outside tap.
 *
 * Escape is handled in the capture phase and stopped when the panel is open,
 * so it closes the panel rather than the whole modal underneath it — the same
 * Bootstrap-modal gotcha legacy hit with its account picker
 * (docs/legacy/webledger.ux-audit-accounts.md).
 */
/**
 * Whether a split needs a second number — its amount in its own currency.
 *
 * The rule legacy states (SPEC.md `tx_save`): a single-currency account whose
 * currency IS the transaction's needs no second number, because the one
 * number says everything. Everything else does.
 *
 * The multi-currency half of that is what this exists to make explicit. Such
 * an account's `currencyCode` is only its *nominal* one — a split on it may be
 * in any currency — so comparing that code with the transaction's answers a
 * different question than the one being asked. It was compared all the same,
 * and when the two happened to match, the currency picker and the second
 * number were swallowed: a conversion inside one multi-currency account
 * (150 USD out, 46,500 HUF in) drew as two plain HUF legs with its other
 * side nowhere on screen.
 */
export function needsAccountCurrencyAmount(
  account: { currencyCode: string; isMulticurrency: boolean }, txCurrency: string,
): boolean {
  return account.isMulticurrency || account.currencyCode !== txCurrency;
}

/**
 * What a leg's account-currency amount is, given what the box holds.
 *
 * An empty box is not a zero, and it means two different things that are not
 * interchangeable:
 *
 *  - on a leg whose currency IS the transaction's, the number is the value.
 *    There is nothing to ask for, so it is filled in. That is also why the
 *    column says "same currency" and hides itself in the simple case.
 *  - on a leg in another currency it is the half of an exchange rate that
 *    only a person can supply — two legs of -100.00 EUR and +38,500 HUF *are*
 *    the rate, which is why nothing stores one — so the save stops and asks.
 *
 * It used to read `Number(box.value) || 0` for both, which saved the leg with
 * an amount of 0 against a value of ±3,000: the transaction still balanced,
 * the account's own balance did not move, and nothing said so.
 */
export function accountCurrencyAmount(
  typed: string, splitCurrency: string, txCurrency: string, value: number,
): { amount: number } | { missing: true } {
  const trimmed = typed.trim();
  if (trimmed !== '') return { amount: Number(trimmed) || 0 };
  if (splitCurrency === txCurrency) return { amount: Math.abs(value) };
  return { missing: true };
}

/**
 * What to put in the box when it first appears, or nothing.
 *
 * Changing a leg to a multi-currency account reveals this column with the
 * transaction's own currency already selected and an empty box — a question
 * with exactly one possible answer, since in that currency the amount IS the
 * value the debit and credit columns are already showing.
 *
 * Only when they match, and only when there is a value to copy. A leg in
 * another currency is genuinely unknown here and a confident default would be
 * the worst kind of wrong.
 */
export function prefillAccountCurrencyAmount(
  splitCurrency: string | undefined, txCurrency: string, value: number,
): string | undefined {
  if ((splitCurrency ?? txCurrency) !== txCurrency) return undefined;
  return value === 0 ? undefined : String(Math.abs(value));
}

/**
 * The currency a new entry balances in: the one its money is in.
 *
 * The first money leg the money LEAVES (a credit), else the first money leg,
 * else the first leg with an account at all. `undefined` when no leg has one
 * yet. See `autoTxCurrency` in the editor for when this is asked.
 */
export function balanceCurrencyFor(
  legs: ReadonlyArray<{ currencyCode: string | undefined; isMoney: boolean; leaving: boolean }>,
): string | undefined {
  const known = legs.filter((l) => l.currencyCode !== undefined);
  const money = known.filter((l) => l.isMoney);
  return (money.find((l) => l.leaving) ?? money[0] ?? known[0])?.currencyCode;
}

/**
 * The wallets a transaction's money legs touch.
 *
 * Whether the "between wallets" question is worth asking at all: one wallet
 * (or none) is an ordinary entry and the question has no meaning, so it is
 * not put. Two or more and it does.
 *
 * Distinct and in the order they appear, because this also fills the two
 * pickers, and a list that reorders itself as you type is one you cannot
 * point at.
 */
export function walletsTouched(
  legs: ReadonlyArray<{ walletId?: string | null }>,
): string[] {
  const seen: string[] = [];
  for (const leg of legs) {
    const wallet = leg.walletId ?? '';
    if (wallet !== '' && !seen.includes(wallet)) seen.push(wallet);
  }
  return seen;
}

/**
 * A first guess at which way the money went, for filling the pickers.
 *
 * A guess and nothing more — the person confirms it, and this exists only so
 * the common case needs no clicks. It nets each wallet, so a leg in and a leg
 * out of the same wallet cancel, and answers only when exactly one wallet
 * ends down and one ends up.
 *
 * **Netting is why this cannot be the answer on its own**, and the shape that
 * proved it is the one this feature was asked for: Revolut −996.96 in
 * Maintenance, −996.96 in Renovation, +996.96 in Maintenance — Maintenance
 * buys the mattress and Renovation lends it the money. Maintenance nets to
 * zero, so the loan is invisible to any amount of arithmetic over the legs.
 * The legs record what each wallet is worth afterwards; which of them was
 * doing the lending is a fact about intent, and intent has to be said.
 */
export function likelyTransfer(
  legs: ReadonlyArray<{ walletId?: string | null; value: string | number }>,
): { fromWalletId: string; toWalletId: string } | undefined {
  const net = new Map<string, number>();
  for (const leg of legs) {
    const wallet = leg.walletId ?? '';
    if (wallet === '') continue;
    net.set(wallet, (net.get(wallet) ?? 0) + Number(leg.value || 0));
  }
  // A hair above zero rather than exactly: decimal strings summed as floats
  // land a cancelled pair on 1e-13.
  const out = [...net].filter(([, v]) => v < -0.00005).map(([id]) => id);
  const into = [...net].filter(([, v]) => v > 0.00005).map(([id]) => id);
  if (out.length !== 1 || into.length !== 1) return undefined;
  return { fromWalletId: out[0]!, toWalletId: into[0]! };
}

export function wireSplitHelp(root: HTMLElement): void {
  wireHelpBubble(root, 'txHelpBtn', 'txHelpPanel');
}

/** The glyph, so every help button on the page is the same one. */
export const HELP_BUBBLE_ICON = HELP_ICON;

/**
 * A help button and the note it reveals.
 *
 * Taken out of `wireSplitHelp` when the short form grew one: the behaviour —
 * toggle, close on an outside click, close on Escape and give focus back —
 * has nothing to do with splits, and a second copy of it would be a second
 * place for the Escape handling to be forgotten.
 */
export function wireHelpBubble(root: HTMLElement, btnId: string, panelId: string): void {
  const btn = root.querySelector<HTMLButtonElement>(`#${btnId}`);
  const panel = root.querySelector<HTMLElement>(`#${panelId}`);
  if (!btn || !panel) return;

  const setOpen = (open: boolean): void => {
    panel.classList.toggle('show', open);
    btn.setAttribute('aria-expanded', String(open));
  };

  btn.addEventListener('click', (event) => {
    event.stopPropagation();
    setOpen(!panel.classList.contains('show'));
  });
  document.addEventListener('click', (event) => {
    if (!panel.classList.contains('show')) return;
    if (!panel.contains(event.target as Node)) setOpen(false);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !panel.classList.contains('show')) return;
    event.stopPropagation();
    setOpen(false);
    btn.focus();
  }, true);
}

interface SplitRowState { id: string; baseVersion?: number; isNew: boolean }

/**
 * `existing === null` is a brand-new transaction: no lease needed at all
 * (ADR-0005) and every split is a `create`. Editing claims the transaction's
 * `$record` lease over the same WebSocket connection this page already holds
 * open. A lease holder is a connection, not a user (ADR-0003's 2026-09-09
 * note) — a denial here means someone (possibly this same user, in another
 * tab) already has it, so this is the *same* form rendered read-only and
 * live-updating instead of refusing to open at all — never a silent no-op,
 * and never a separately-built "summary" layout that looks different from
 * the real thing. Reverts back to a normal editable form in place, without
 * closing the modal, the moment the other holder's lease is freed. Only
 * genuine offline still refuses outright — there's nothing live to show. On
 * success, loads fresh data (never trusts the cached list: `baseVersion`
 * must be current at the moment of claim) and releases the lease whenever
 * the modal closes, saved or not.
 */
export async function openTransactionEditor(
  root: HTMLElement, client: WebledgerClient,
  existing: TransactionDto | null, accounts: AccountDto[], currencies: CurrencyDto[],
  /**
   * Fired whenever the editor has finished with a transaction — saved,
   * queued offline, restored from history, or simply closed. The id is the
   * one it was open on, which is what a list needs to point back at the row
   * afterwards; a caller that only reloads can keep ignoring it.
   */
  onSaved: (txId: string) => void,
  /**
   * `openHistory` opens with the change history already showing — for the
   * short form handing a version over that it has no way to draw, so the
   * reader arrives looking at the thing they asked for rather than having to
   * find it again.
   *
   * `simpleEditor` is the way back. It is a callback rather than a flag
   * because the short form belongs to the wallets page: every other page
   * that opens this editor has nowhere to hand back to, and the button stays
   * hidden there rather than offering something that cannot happen. The
   * caller decides whether this particular transaction is a shape the short
   * form can say — it owns that test — and passes nothing when it is not.
   */
  opts: {
    openHistory?: boolean;
    simpleEditor?: (txId: string) => void;
    /**
     * Stepping to the transaction before or after this one in the list the
     * form was opened from.
     *
     * The caller supplies it, because "the list" is not one thing: the
     * wallet log is built from the replica and filtered by wallet and tag,
     * the register is fetched per account, the report is whatever the
     * filters left. Only the page that drew the list knows what is next, and
     * only it can mark the row and scroll it into view afterwards.
     *
     * `idOf` returning nothing is how an end of the list is said; the button
     * is disabled rather than hidden, so the pair does not move about as you
     * walk through.
     */
    neighbours?: {
      idOf: (txId: string, delta: -1 | 1) => string | undefined;
      go: (txId: string) => void;
    };
    /**
     * Unsaved values this tab had in the form when the browser discarded it
     * (webledgerFormRestore.ts) — put back as they were, or, if somebody has
     * the entry by now, kept beside their version as a held draft.
     */
    restore?: QueuedTransactionPayload;
  } = {},
): Promise<void> {
  const { token, leaseClient, notify, transport, session, userId, docCache, outbox, prefs } = client;
  // Opening is the retry for whatever failed to open last time, so a notice
  // left on the page behind this modal is spent.
  clearPageNotice(root);
  const modalEl = root.querySelector<HTMLElement>('#txModal')!;
  const titleEl = root.querySelector<HTMLElement>('#txModalTitle')!;
  const form = root.querySelector<HTMLFormElement>('#txForm')!;
  const rowsEl = root.querySelector<HTMLElement>('#txSplitRows')!;
  const balanceEl = root.querySelector<HTMLElement>('#txBalance')!;
  const noticeEl = root.querySelector<HTMLElement>('#txReadOnlyNotice')!;
  const heldBannerEl = root.querySelector<HTMLElement>('#txHeldBanner')!;
  const heldFieldsEl = root.querySelector<HTMLElement>('#txHeldFields')!;
  const footerNoteEl = root.querySelector<HTMLElement>('#txReadOnlyFooterNote')!;
  const errorEl = root.querySelector<HTMLElement>('#txError')!;

  /**
   * A failure the user can see, inside the modal they are looking at.
   *
   * Everything here used to go to `notify` alone, which writes to the `.cf-sr`
   * region — clipped to a 1px box, announced and otherwise invisible. A save
   * that failed therefore looked exactly like a save that worked, right down
   * to the form sitting there unchanged, which is the worst of these by some
   * margin. Both still happen: `notify` for the announcement, `errorEl` for
   * the eyes, and the glyph and wording carry the meaning so the red is never
   * the only signal (invariant #11).
   */
  const showError = (message: string): void => {
    notify(message);
    errorEl.innerHTML = `<span aria-hidden="true">\u26a0</span> ${escapeHtml(message)}`;
    errorEl.hidden = false;
  };
  /** Cleared whenever the act that failed is attempted again, so it never outlives its cause. */
  const clearError = (): void => { errorEl.hidden = true; errorEl.textContent = ''; };
  clearError(); // the modal element is reused across openings
  const saveBtn = root.querySelector<HTMLButtonElement>('#txSaveBtn')!;
  const navEl = root.querySelector<HTMLElement>('#txNav')!;
  const prevBtn = root.querySelector<HTMLButtonElement>('#txPrevBtn')!;
  const nextBtn = root.querySelector<HTMLButtonElement>('#txNextBtn')!;
  const addSplitBtn = root.querySelector<HTMLButtonElement>('#txAddSplit')!;
  const mergeBtn = root.querySelector<HTMLButtonElement>('#txMergeBtn')!;
  const deleteBtn = root.querySelector<HTMLButtonElement>('#txDeleteBtn')!;
  const mergePanel = root.querySelector<HTMLElement>('#txMergePanel')!;
  /**
   * The file row's elements, declared **here** with every other element
   * handle rather than beside the code that uses them.
   *
   * `setFormReadOnly` is a hoisted function declaration and runs long before
   * the bottom of this function; a `const` declared down there is in its
   * temporal dead zone, so touching it threw `Cannot access 'filesBar' before
   * initialization` — inside an `async` mount whose rejection nothing was
   * listening to. The modal simply never opened, and a reload did the same
   * thing again. That is the exact shape of an earlier report about the
   * register, and the reason element handles all live together up here.
   */
  const moveBar = root.querySelector<HTMLElement>('#txMove')!;
  const moveRowsEl = root.querySelector<HTMLElement>('#txMoveRows')!;
  const moveAddEl = root.querySelector<HTMLButtonElement>('#txMoveAdd')!;
  /** The sidecar as loaded, so a save can tell a changed answer from an untouched one. */
  /** One "Between wallets" row, as the form holds it and the save sends it. */
  interface WalletMove {
    id: string; kind: string; fromWalletId: string; toWalletId: string; amountSplitId: string;
    /** Part of the leg, typed; null for all of it. */
    amount: string | null;
  }
  let loadedMoves: WalletMove[] = [];

  const tagsBar = root.querySelector<HTMLElement>('#txTags')!;
  const tagListEl = root.querySelector<HTMLElement>('#txTagList')!;
  const tagPickEl = root.querySelector<HTMLSelectElement>('#txTagPick')!;
  const tagNoteEl = root.querySelector<HTMLElement>('#txTagNote')!;
  let allTags: TagDto[] = [];
  const tagSelection = emptyTagSelection();
  /** Set once the probe finds this transaction really does have a bank leg. */
  let mergeable = false;
  const dateInput = form.elements.namedItem('txDate') as HTMLInputElement;
  // Our own date control rather than the browser's — see dateField.ts. The
  // input keeps its name and its ISO value, so everything that reads it is
  // untouched.
  const dateField = mountDateField(
    dateInput,
    root.querySelector<HTMLElement>('[data-date-open="txDateInput"]')!,
  );
  const referenceInput = form.elements.namedItem('reference') as HTMLInputElement;
  const descriptionInput = form.elements.namedItem('description') as HTMLInputElement;
  const currencySelect = form.elements.namedItem('currencyCode') as HTMLSelectElement;
  const removedSplitIds = new Set<string>();

  const bookableAccounts = accounts.filter((a) => !a.isPlaceholder);
  const txId = existing?.id ?? crypto.randomUUID();
  const leaseKey = recordLeaseKey('transaction', txId);
  /**
   * This form's hold on the record, and everything that happens to it — shared
   * with the short form (webledgerHold.ts). Created only for a transaction
   * that exists; a new one is nobody else's to hold (ADR-0005).
   */
  let hold: RecordHold | undefined;
  let holdView: HoldView = { kind: 'claiming' };
  /**
   * Set once the whole form is built — the history panel included, which is
   * declared further down. The hold can speak during the open flow's awaits;
   * a record freed in that window is picked up the moment the form is ready
   * rather than reaching into a `const` still in its temporal dead zone (the
   * mistake this file has made before, see the note on `filesBar`).
   */
  let formReady = false;
  let freedBeforeReady = false;
  /** The token to save under, read fresh each time: it changes hands on a take-over. */
  const heldLeaseId = (): string | undefined => hold?.leaseId();
  let baseVersion = 0;
  let tx = existing;
  let readOnly = false;
  /**
   * Read-only because a past version is on screen — not because this person
   * may not edit this transaction.
   *
   * The two are the same switch and they are not the same fact. The history
   * panel asks `canEdit` to decide whether to offer a restore, and viewing a
   * version flipped that switch, so the one action you would want after
   * looking at an old version was the one thing looking at it took away.
   */
  let readOnlyForViewing = false;

  /**
   * Files. The bar is mounted rather than wired here, because the short form
   * has one too now and none of listing/attaching/removing is particular to
   * either.
   *
   * Mounted HERE, high up, and not beside the rest of the file handling
   * further down. `setFormReadOnly` repaints it, and `setFormReadOnly` runs
   * during this function's own setup — a `const` declared after that point is
   * still in its temporal dead zone when it is read, and the failure is a
   * bare "Cannot access before initialization" from inside a callback, a
   * long way from the declaration that caused it. This file has been caught
   * by that twice before; anything the setup path touches belongs above it.
   */
  /**
   * Files. The bar is mounted rather than wired here, because the short form
   * has one too now and none of listing/attaching/removing is particular to
   * either.
   */
  const attachments = mountAttachmentBar(root, 'tx', {
    // Tiles from the local replica first, so they show offline too (ADR-0025):
    // name, size and thumbnail are on the device; only the signed url is not.
    localFiles: (txId2) => attachmentsOf(datasetLedger(client.dataset), txId2).map((a) => ({
      id: a.id, transactionId: txId2, originalName: a.originalName, mime: a.mime,
      size: a.size ?? 0, thumb: a.thumb ?? '', created: null, url: '',
    })),
    txId: () => (existing ? txId : undefined),
    token,
    canEdit: () => !readOnly,
    // A new entry holds its files until Save has created it.
    holdUntilSaved: () => !existing && !readOnly,
    notify,
  });
  /** Set while this form is showing a copy saved on this device, with no connection to check it against. */
  let offlineSince: string | undefined;
  /**
   * A draft of this record that somebody interrupted by taking the lease.
   *
   * Durable (it lives in the outbox), so it survives a reload and shows up in
   * the register as a marker on the row. Never sent by the flusher — see
   * `sendableDocuments`. It leaves only when its owner applies it or throws
   * it away, because it is the one thing on screen nobody else can reproduce.
   */
  let heldDraft: OutboxDocumentWrite | undefined;
  /** Who took it, for the banner. Separate from the hold: they may have since let go. */
  let stolenBy: PresenceUser | undefined;
  /**
   * Who *you* took it from. The other half of the same event, and the half
   * that does the social work: knowing you interrupted a named person, who
   * still holds unsaved changes and can take it straight back, is what stops
   * a take-over turning into a fight nobody understands.
   */
  let tookFrom: PresenceUser | undefined;
  const dateInputHandler = (): void => previewTransactionField('txDate', { t: 'date', v: dateInput.value });
  const referenceInputHandler = (): void => previewTransactionField('reference', { t: 'string', v: referenceInput.value });
  const descriptionInputHandler = (): void => previewTransactionField('description', { t: 'string', v: descriptionInput.value });
  dateInput.addEventListener('input', dateInputHandler);
  referenceInput.addEventListener('input', referenceInputHandler);
  descriptionInput.addEventListener('input', descriptionInputHandler);

  const currenciesByCode = new Map(currencies.map((c) => [c.code, c]));
  const sortedCurrencyCodes = [...currenciesByCode.keys()].sort((a, b) => a.localeCompare(b));
  currencySelect.innerHTML = sortedCurrencyCodes.map((code) => `<option value="${code}">${escapeHtml(code)}</option>`).join('');

  const rows: SplitRowState[] = [];

  function applyRowReadOnly(tr: HTMLTableRowElement, on: boolean): void {
    applyFieldReadOnly(tr.querySelector('[data-split-memo]'), on);
    applyFieldReadOnly(tr.querySelector('[data-split-debit]'), on);
    applyFieldReadOnly(tr.querySelector('[data-split-credit]'), on);
    applyFieldReadOnly(tr.querySelector('[data-split-amount]'), on);
    applySelectReadOnly(tr.querySelector('[data-split-account]'), on);
    applySelectReadOnly(tr.querySelector('[data-split-wallet]'), on);
    applySelectReadOnly(tr.querySelector('[data-split-currency]'), on);
    applyButtonReadOnly(tr.querySelector('[data-split-account-btn]'), on);
    const removeBtn = tr.querySelector<HTMLElement>('[data-split-remove]');
    if (removeBtn) removeBtn.hidden = on;
  }

  /**
   * Flips the whole modal between a normal editable form and a read-only,
   * live-updating one — the *same* markup and layout either way, just with
   * every control's interactivity toggled, never a different-looking
   * "summary" view. Called before `populate()` on every mode change so
   * freshly-built rows are already correct, and it still fixes up any
   * already-rendered rows itself (defensive: correct regardless of call order).
   */
  function setFormReadOnly(on: boolean): void {
    readOnly = on;
    applyFieldReadOnly(dateInput, on);
    applyFieldReadOnly(referenceInput, on);
    applyFieldReadOnly(descriptionInput, on);
    applySelectReadOnly(currencySelect, on);
    addSplitBtn.hidden = on;
    saveBtn.hidden = on;
    // A merge rewrites both transactions, so it is not something to offer on
    // a form you are only watching. The panel closes with it, or it would sit
    // there listing choices that cannot be made.
    if (on) closeMergePanel();
    mergeBtn.hidden = on || !mergeable;
    // Never offered for a transaction that does not exist yet (there is
    // nothing to delete — Cancel is that), nor on a form you are only
    // watching, where the owner is mid-edit and would lose their work.
    deleteBtn.hidden = on || !existing;
    renderTags();
    // `readonly`, never `disabled` (invariant #4): on a form being watched
    // the answer stays readable and keeps its place in the tab order.
    moveRowsEl.querySelectorAll<HTMLSelectElement>('select').forEach((sel) => {
      applySelectReadOnly(sel, on);
    });
    // A button has no readonly, and a lock that leaves Add and Remove live
    // would let somebody rearrange a record they cannot save (invariant #4
    // is about FIELDS keeping their place in the tab order; these are
    // actions, and an action that cannot happen is correctly disabled).
    moveAddEl.disabled = on;
    moveRowsEl.querySelectorAll<HTMLButtonElement>('[data-move-remove]').forEach((b) => {
      b.disabled = on;
    });
    attachments.render();
    footerNoteEl.hidden = !on;
    noticeEl.hidden = !on;
    rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]').forEach((tr) => applyRowReadOnly(tr, on));
    // "Keep mine" only exists on a form you can type into, so the markers are
    // repainted rather than left showing a button that would do nothing.
    if (heldDraft) paintHeldMarkers();
  }

  /** Whoever we know about, preferring live presence (freshest colour/name) over what the record carried. */
  function personFor(id: string): PresenceUser | undefined {
    return session.presence('transaction', txId).find((p) => p.id === id)
      ?? (holdView.kind === 'theirs' && holdView.holder.userId === id ? holdView.holder.person : undefined)
      ?? (tx?.ownedBy?.id === id ? tx.ownedBy : undefined);
  }

  /**
   * The editable-but-offline state, which is neither of the read-only states
   * `updateNotice` handles. ADR-0011: "Offline editing happens without a
   * lock, which is a different state from holding a lease. The UI must say so
   * ('editing offline, not reserved') or users assume the same protection
   * they get online." So it says exactly that, and warns that a concurrent
   * change means a review rather than a silent win.
   */
  function renderOfflineEditNotice(hasQueuedEdit = false): void {
    noticeEl.hidden = false;
    // Whether the record stays this tab's while the connection is away: it
    // does if this tab held it — holds no longer lapse (ADR-0003,
    // 2026-10-22) — unless somebody takes it over meanwhile.
    const reserved = holdView.kind === 'offline' && holdView.held;
    noticeEl.innerHTML =
      (reserved
        ? '<strong>Editing offline — still reserved for you</strong>, unless somebody takes it over meanwhile. '
        : '<strong>Editing offline — not reserved.</strong> ') +
      (hasQueuedEdit
        ? `Showing your unsent edit from this device. `
        : `Based on the copy saved on this device ${escapeHtml(ageLabel(offlineSince))}. `) +
      `Saving stores it here and syncs when the connection returns. ` +
      `If this transaction changes before then, your version is held for you to apply or discard — ` +
      `never applied over theirs.`;
  }

  const accountLabel = (id: string): string =>
    accounts.find((a) => a.id === id)?.name ?? id.slice(0, 8);

  /**
   * Snapshot what is on screen, because somebody just took the record.
   *
   * Only if it actually differs from what was loaded: taking over from
   * someone who opened a record and typed nothing should leave nothing
   * behind, or every take-over would litter the register with markers about
   * drafts that say the same as the record.
   *
   * The whole document is held, not the changed fields. A transaction's legs
   * have to balance, so "yours" and "theirs" are two complete candidates;
   * splicing them together could produce an entry that balances in neither
   * version. Same reasoning as the offline pending revision (ADR-0011), and
   * the same store, because it is the same thing: unsent work of yours that
   * a human has to decide about.
   */
  async function holdDraft(thief: PresenceUser | undefined): Promise<void> {
    if (!existing) return;                       // a create is nobody else's to take
    const { body } = readForm();
    const loaded = tx ? transactionToPayload(tx) : undefined;
    if (loaded && heldDifferences(body, loaded, accountLabel).length === 0) return;

    heldDraft = await outbox.enqueueDocument({
      entity: 'transaction', recordId: txId, op: 'update', payload: body,
      clock: session.nextClock(),
      held: {
        takenBy: thief?.id ?? (holdView.kind === 'theirs' ? holdView.holder.userId : userId),
        takenByName: thief?.name ?? 'Someone',
        at: new Date().toISOString(),
      },
    });
  }

  /** The DTO as a save body, so a held draft can be compared against it. */
  function transactionToPayload(t: TransactionDto): QueuedTransactionPayload {
    return {
      id: t.id, txDate: t.txDate, reference: t.reference, description: t.description,
      currencyCode: t.currencyCode, baseVersion: t.version,
      splits: t.splits.map((sp) => ({
        id: sp.id, accountId: sp.accountId, memo: sp.memo,
        value: sp.value, amount: sp.amount, currencyCode: sp.currencyCode,
        walletId: sp.walletId ?? null,
        baseVersion: sp.version,
      })),
    };
  }

  /** Wipe every "yours, held" marker before repainting them. */
  function clearHeldMarkers(): void {
    heldFieldsEl.innerHTML = '';
    rowsEl.querySelectorAll('tr.wl-held-row').forEach((el) => el.remove());
  }

  /**
   * Paint the held draft beside the values that replaced it.
   *
   * Only on the controls that differ, which is the point: touch three fields
   * of seventeen and three markers appear, so the amount of form given over
   * to the conflict is exactly the size of what you stand to lose. Everything
   * else keeps streaming the new holder's typing like any read-only view.
   */
  function paintHeldMarkers(): number {
    clearHeldMarkers();
    if (!heldDraft) { renderBanner(0); return 0; }

    const mine = heldDraft.payload as QueuedTransactionPayload;
    const diffs = heldDifferences(mine, readForm().body, accountLabel);
    renderBanner(diffs.length);
    for (const diff of diffs) attachHeldMarker(diff);
    return diffs.length;
  }

  function heldStripHtml(diff: HeldDifference): string {
    const who = stolenBy
      ? `<span class="wl-held__who">${presenceDotHtml(stolenBy)} ${escapeHtml(stolenBy.name)} has it</span>`
      : '';
    // "Keep mine" only means anything on a form you can type into. Read-only,
    // the marker is there to be read, and the way back in is Take it back.
    const keep = readOnly
      ? ''
      : `<button type="button" class="btn btn-warning" data-keep-mine="${escapeHtml(diff.where)}">Keep mine</button>`;
    return `<span aria-hidden="true">⚠</span><b>${escapeHtml(diff.label)}, yours</b>${who}` +
      `<span class="wl-held__v" title="${escapeHtml(diff.mine)}">${escapeHtml(diff.mine)}</span>${keep}`;
  }

  function attachHeldMarker(diff: HeldDifference): void {
    if (!diff.where.startsWith('split:')) {
      const strip = document.createElement('div');
      strip.className = 'wl-held';
      strip.innerHTML = heldStripHtml(diff);
      heldFieldsEl.appendChild(strip);
      return;
    }
    const id = diff.where.slice('split:'.length);
    const tr = rowsEl.querySelector<HTMLTableRowElement>(`tr[data-row-id="${CSS.escape(id)}"]`);
    const strip = document.createElement('tr');
    strip.className = 'wl-held-row';
    strip.innerHTML = `<td colspan="6"><div class="wl-held wl-held--row">${heldStripHtml(diff)}</div></td>`;
    // A leg you removed has no row to hang under, so it goes at the end
    // rather than being silently dropped.
    if (tr) tr.insertAdjacentElement('afterend', strip);
    else rowsEl.appendChild(strip);
  }

  /**
   * Both sides of a take-over, in the same place.
   *
   * Told plainly, because the thing people actually need is not a warning but
   * a fact and a name: who has it, that nothing of yours was lost, and that
   * the other person can see what you are doing. Two people who know that
   * much sort it out between themselves in one exchange; two people guessing
   * do not.
   */
  function renderBanner(count: number): void {
    if (heldDraft) {
      const who = stolenBy ? `<strong>${escapeHtml(stolenBy.name)}</strong>` : 'Someone';
      const dot = stolenBy ? `${presenceDotHtml(stolenBy)} ` : '';
      const mine = count === 1 ? '1 unsaved change' : `${count} unsaved changes`;
      heldBannerEl.className = 'alert alert-warning py-2 small mb-3';
      heldBannerEl.hidden = false;
      heldBannerEl.innerHTML =
        `${dot}${who} took this transaction over. Your ${mine} ${count === 1 ? 'is' : 'are'} kept — ` +
        `nothing was lost, and the form below shows what ${stolenBy ? escapeHtml(stolenBy.name) : 'they'} ` +
        `${readOnly ? 'is doing, live' : 'did'}.` +
        (readOnly
          ? ` <button type="button" class="btn btn-sm btn-warning ms-2" id="txTakeBackBtn">Take it back</button>`
          : ` <button type="button" class="btn btn-sm btn-warning ms-2" id="txKeepAllMineBtn">Keep all mine</button>`) +
        ` <button type="button" class="btn btn-sm btn-outline-secondary ms-1" id="txDiscardMineBtn">Discard mine</button>`;
      return;
    }
    if (tookFrom) {
      heldBannerEl.className = 'alert alert-info py-2 small mb-3';
      heldBannerEl.hidden = false;
      heldBannerEl.innerHTML =
        `${presenceDotHtml(tookFrom)} You took this transaction over from <strong>${escapeHtml(tookFrom.name)}</strong>, ` +
        `who was part-way through an edit. They can see what you type, and can take it back.`;
      return;
    }
    heldBannerEl.hidden = true;
  }

  /** Put one held value back into the form. Editable only — see `heldStripHtml`. */
  function applyHeldValue(where: string): void {
    if (!heldDraft || readOnly) return;
    const mine = heldDraft.payload as QueuedTransactionPayload;
    if (where === 'txDate') dateInput.value = mine.txDate;
    else if (where === 'reference') referenceInput.value = mine.reference ?? '';
    else if (where === 'description') descriptionInput.value = mine.description ?? '';
    else if (where === 'currencyCode') currencySelect.value = mine.currencyCode;
    else if (where.startsWith('split:')) applyHeldSplit(where.slice('split:'.length));
    // Nothing left to decide means nothing left to hold. Without this the
    // banner sat there afterwards announcing "your 0 unsaved changes are
    // kept", and the register kept a marker on a row with nothing behind it.
    if (paintHeldMarkers() === 0) {
      void releaseHeldDraft();
      notify('Your changes are back in the form. Save to keep them.');
    }
  }

  function applyHeldSplit(id: string): void {
    const mine = heldDraft && (heldDraft.payload as QueuedTransactionPayload).splits.find((sp) => sp.id === id);
    if (!mine) return;
    const tr = rowsEl.querySelector<HTMLTableRowElement>(`tr[data-row-id="${CSS.escape(id)}"]`);
    if (mine.op === 'delete') {
      if (tr) {
        removedSplitIds.add(id);
        tr.remove();
        updateTotals();
      }
      return;
    }
    if (!tr) {                                   // a leg of yours they never had
      addRow({
        id: mine.id, transactionId: txId, accountId: mine.accountId, memo: mine.memo ?? '',
        value: mine.value, amount: mine.amount, currencyCode: mine.currencyCode, version: 0,
      });
      updateTotals();
      return;
    }
    const value = Number(mine.value);
    (tr.querySelector('[data-split-account]') as HTMLSelectElement).value = mine.accountId;
    (tr.querySelector('[data-split-memo]') as HTMLInputElement).value = mine.memo ?? '';
    (tr.querySelector('[data-split-debit]') as HTMLInputElement).value = value > 0 ? String(value) : '';
    (tr.querySelector('[data-split-credit]') as HTMLInputElement).value = value < 0 ? String(-value) : '';
    refreshAmountCell(tr, mine.amount, mine.currencyCode);
    // The account may have changed with the draft, and the row's wording and
    // its picker button both follow from it.
    updateSplitLabels(tr);
    updateTotals();
  }

  /** The draft has been applied or abandoned; stop holding it. */
  async function releaseHeldDraft(): Promise<void> {
    if (!heldDraft) return;
    await outbox.removeDocument(heldDraft.id);
    heldDraft = undefined;
    stolenBy = undefined;
    clearHeldMarkers();
    renderBanner(0);
  }

  function updateNotice(): void {
    if (!readOnly) return;

    // Offline outranks everything below: whatever hold this copy shows was
    // true when it was saved, and saying "X is editing this" from a cached
    // snapshot would be asserting something we cannot know.
    if (offlineSince) {
      noticeEl.innerHTML =
        `<strong>Offline.</strong> This is the copy saved on this device ${escapeHtml(ageLabel(offlineSince))}, ` +
        `shown read-only. Who is editing it may be out of date. ` +
        `It will become editable on its own when the connection comes back.`;
      return;
    }
    // Who has it, since when, whether they are connected now, and the way in
    // — the same wording the short form uses (webledgerHold.ts).
    noticeEl.innerHTML = holdNoticeHtml(holdView, 'transaction', 'txTakeOverBtn')
      || 'This transaction is read-only right now.';
  }

  /**
   * Live "what's being typed" mirror for other viewers — never persisted
   * (matches the wire protocol's own `preview` doc comment), sent on every
   * input/change while editing. Field names here are UI-local, not manifest
   * field names: a split row doesn't exist as a persisted record until save,
   * and `value`/`amount`'s save-time sign derivation from separate debit/
   * credit inputs has no bearing on what's literally being typed, so this
   * mirrors the raw UI fields (`debit`, `credit`, `splitCurrency`, ...)
   * directly rather than the shape `save()` eventually sends.
   *
   * A split preview is folded into the *transaction's own* field key
   * (`split_<rowId>_<subfield>`) rather than sent under a separate `split`
   * entity/recordId — the server only broadcasts a `preview` to occupants of
   * that exact (entity, recordId) (`ctx.registry.occupants`, services/sync's
   * `handleMessage`), and nothing anywhere ever calls `openRecord`/
   * `subscribe` for an individual split, only for the parent transaction
   * (both sides already do that, for presence). A `split:<id>` preview would
   * therefore broadcast to zero occupants and silently vanish — confirmed by
   * testing it that way first. Reusing the transaction's own occupancy is
   * simpler than adding per-split subscription bookkeeping for something
   * this ephemeral.
   */
  function previewTransactionField(field: string, value: FieldValue): void {
    if (readOnly) return;
    session.preview('transaction', txId, field, value);
  }
  function previewSplitField(rowId: string, field: string, value: FieldValue): void {
    if (readOnly) return;
    session.preview('transaction', txId, `split_${rowId}_${field}`, value);
  }

  /**
   * The read-only counterpart to `previewField` — applied only while
   * `readOnly`, and only ever via direct property writes, which fire no
   * `input`/`change` events of their own, so this can never loop back into
   * sending a preview. Only updates a split row that's already rendered: a
   * row the editor has added but not yet saved has nothing here to update
   * yet — the structural change itself only becomes visible on save (or on
   * the periodic `recordEvent` refresh), not per keystroke. That's a
   * deliberate scope line, not an oversight: mirroring in-progress row
   * add/remove live would need a good deal more bookkeeping for a case that
   * mostly matters for the seconds before a save, not the typing itself.
   */
  function applyLivePreview(msg: Extract<ServerMessage, { t: 'preview' }>): void {
    if (!readOnly) return;
    const { entity, recordId, field } = parseFieldKey(msg.key);
    if (entity !== 'transaction' || recordId !== txId) return;
    const text = msg.value.t === 'null' ? '' : String(msg.value.v);

    if (field.startsWith('split_')) {
      const rest = field.slice('split_'.length);
      const sep = rest.lastIndexOf('_'); // rowId (a uuid, no underscores) then subfield (also none)
      if (sep === -1) return;
      const rowId = rest.slice(0, sep);
      const subfield = rest.slice(sep + 1);
      const tr = rowsEl.querySelector<HTMLTableRowElement>(`tr[data-row-id="${rowId}"]`);
      if (!tr) return;
      if (subfield === 'memo') (tr.querySelector('[data-split-memo]') as HTMLInputElement).value = text;
      else if (subfield === 'debit') { (tr.querySelector('[data-split-debit]') as HTMLInputElement).value = text; updateTotals(); }
      else if (subfield === 'credit') { (tr.querySelector('[data-split-credit]') as HTMLInputElement).value = text; updateTotals(); }
      else if (subfield === 'amount') { const el = tr.querySelector<HTMLInputElement>('[data-split-amount]'); if (el) el.value = text; }
      else if (subfield === 'accountId') {
        const sel = tr.querySelector<HTMLSelectElement>('[data-split-account]')!;
        sel.value = msg.value.t === 'uuid' ? msg.value.v : '';
        refreshAmountCell(tr);
        updateSplitLabels(tr);
        updateTotals();
      } else if (subfield === 'splitCurrency') {
        const sel = tr.querySelector<HTMLSelectElement>('[data-split-currency]');
        if (sel) sel.value = text;
      }
      return;
    }

    if (field === 'txDate') dateInput.value = text;
    else if (field === 'reference') referenceInput.value = text;
    else if (field === 'description') { descriptionInput.value = text; titleEl.textContent = text || 'Transaction'; }
    else if (field === 'currencyCode') {
      currencySelect.value = text;
      root.querySelectorAll<HTMLElement>('.txCurLabel').forEach((el) => { el.textContent = `(${text})`; });
      rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]').forEach((tr) => refreshAmountCell(tr));
      paintTxCurrency();
      updateTotals();
    }
  }

  /**
   * The wallets, for the per-leg picker (ADR-0023).
   *
   * Read here rather than passed in, so every existing caller of
   * `openTransactionEditor` keeps its signature. Until it resolves the picker
   * shows only the current value, which is the honest intermediate state: it
   * never silently offers "Unassigned" as though that were the answer.
   */
  const walletsById = new Map<string, { id: string; name: string; parentId: string | null }>();
  let walletsLoaded = false;

  function walletPathOf(id: string): string {
    const parts: string[] = [];
    let cursor = walletsById.get(id);
    let guard = 0;
    while (cursor && guard < 32) {
      parts.unshift(cursor.name);
      cursor = cursor.parentId ? walletsById.get(cursor.parentId) : undefined;
      guard += 1;
    }
    return parts.join(' / ');
  }

  /** Only a leg that holds money carries a wallet — see the note on `split.walletId`. */
  function isMoneyAccount(accountId: string): boolean {
    const type = accounts.find((a) => a.id === accountId)?.type;
    return type === 'ASSET' || type === 'LIABILITY';
  }

  function walletOptionsHtml(selected: string | null | undefined): string {
    const chosen = selected ?? '';
    const options = [...walletsById.values()]
      .map((w) => ({ id: w.id, path: walletPathOf(w.id) }))
      .sort((a, b) => a.path.localeCompare(b.path));
    /*
     * An option for the current value ALWAYS exists, even before the wallet
     * list has arrived and even if that wallet has since been deleted.
     *
     * Without it the select would fall back to an empty value during the one
     * asynchronous read after opening — and a save in that window would send
     * `walletId: null` and silently un-earmark every leg of the transaction.
     * The label is a placeholder for that moment only; `refreshWalletCells`
     * rewrites it with the real path the instant the list lands.
     */
    const known = chosen !== '' && walletsById.has(chosen);
    const placeholder = chosen !== '' && !known
      ? `<option value="${escapeHtml(chosen)}" selected>${walletsLoaded ? 'unknown wallet' : 'loading…'}</option>`
      : '';
    // Full path, never an indent: two wallets may both be called "66".
    return `<option value=""${chosen ? '' : ' selected'}>— not earmarked —</option>`
      + placeholder
      + options.map((w) =>
        `<option value="${escapeHtml(w.id)}"${w.id === chosen ? ' selected' : ''}>${escapeHtml(w.path)}</option>`).join('');
  }

  /** Repaint every row's picker once the wallet list lands. */
  function refreshWalletCells(): void {
    rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]').forEach((tr) => {
      const cell = tr.querySelector<HTMLElement>('[data-wallet-cell]');
      const select = tr.querySelector<HTMLSelectElement>('[data-split-wallet]');
      if (!cell || !select) return;
      const accountId = (tr.querySelector('[data-split-account]') as HTMLSelectElement).value;
      const money = isMoneyAccount(accountId);
      const current = select.value;
      if (walletsLoaded) {
        select.innerHTML = walletOptionsHtml(current || null);
        select.value = current;
      }
      /*
       * Hide the whole notched FIELDSET, not just the select inside it.
       * `notchedFieldHtml` draws a `<fieldset>` with a `<legend>`, so hiding
       * only the control leaves an empty outlined box still labelled WALLET —
       * which reads as a field that has lost its contents rather than one
       * that does not apply here.
       *
       * Hidden rather than disabled, per invariant #4: a category leg simply
       * has no wallet, so the control leaves the tab order and the
       * accessibility tree entirely instead of sitting there unusable.
       */
      const field = select.closest<HTMLElement>('fieldset.wl-nf');
      if (field) field.hidden = !money;
      select.hidden = false;
      const dash = cell.querySelector<HTMLElement>('[data-wallet-na]');
      if (dash) dash.hidden = money;
    });
  }

  void (async () => {
    const read = await readCached<{ wallets: Array<{ id: string; name: string; parentId: string | null }> }>(
      docCache, 'wallets', '/apps/webledger/wallets', token,
    );
    if (!read.ok) return;
    for (const w of read.data.wallets) walletsById.set(w.id, w);
    walletsLoaded = true;
    refreshWalletCells();
  })();

  function accountOptionsHtml(selected: string): string {
    return bookableAccounts.map((a) => `<option value="${a.id}" data-type="${a.type}" ${a.id === selected ? 'selected' : ''}>${escapeHtml(a.name)}</option>`).join('');
  }

  function txCurrency(): string {
    return currencySelect.value;
  }

  /** What currency a row's leg is in, as the form stands. */
  function rowLegCurrency(tr: HTMLTableRowElement): string | undefined {
    const accountId = tr.querySelector<HTMLSelectElement>('[data-split-account]')?.value ?? '';
    const account = bookableAccounts.find((a) => a.id === accountId);
    if (!account) return undefined;
    if (!account.isMulticurrency) return account.currencyCode;
    return tr.querySelector<HTMLSelectElement>('[data-split-currency]')?.value || txCurrency();
  }

  /**
   * The currency field as text, or as a picker when there is a choice.
   *
   * Every leg in the entry's own currency: nothing to choose, so it reads as
   * plain text. Any leg in another: the picker, because now it decides what
   * the debit and credit columns are counted in.
   */
  function paintTxCurrency(): void {
    const fixed = root.querySelector<HTMLElement>('#txCurrencyFixed')!;
    const legs = [...rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]')]
      .map(rowLegCurrency).filter((c): c is string => c !== undefined);
    const mixed = legs.some((c) => c !== txCurrency());
    currencySelect.hidden = !mixed;
    fixed.hidden = mixed;
    fixed.textContent = txCurrency();
  }

  /**
   * Set when the person picks the currency themselves; from then on it is
   * theirs and nothing here moves it.
   */
  let txCurrencyChosen = false;

  /**
   * A NEW entry balances in the currency its money account is in.
   *
   * Only a new one. An existing entry's stored values are in its currency,
   * and re-denominating it would rewrite what each of them means — the rule
   * the short form already keeps. Only on an account being picked, never on
   * typing: switching re-renders every row's second-currency amount, which
   * would take the figures out from under the fingers typing them.
   *
   * The money account is the first row on an asset or liability, preferring
   * one the money leaves (a credit); failing any, the first row there is.
   */
  function autoTxCurrency(): void {
    if (tx?.id || txCurrencyChosen || readOnly) return;
    const code = balanceCurrencyFor([...rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]')]
      .map((tr) => ({
        currencyCode: rowLegCurrency(tr),
        isMoney: isMoneyAccount(tr.querySelector<HTMLSelectElement>('[data-split-account]')?.value ?? ''),
        leaving: Number((tr.querySelector('[data-split-credit]') as HTMLInputElement).value) > 0,
      })));
    if (!code || code === txCurrency() || !currenciesByCode.has(code)) return;
    currencySelect.value = code;
    currencyChangeHandler();
  }

  /** The finest increment worth typing for a currency's own fraction — e.g. "0.01" for 2, "1" for 0. Never a fixed constant (see amountCellHtml's own comment on why). */
  /**
   * Everything in the row that is a consequence of *which account* it names:
   * the two amount columns' wording, and the picker button's own face.
   *
   * Called from every path that writes the hidden select, including the ones
   * that set `.value` directly without firing `change` — applying a held
   * draft, a remote preview, the last-used defaults.
   */
  function updateSplitLabels(tr: HTMLTableRowElement): void {
    const select = tr.querySelector<HTMLSelectElement>('[data-split-account]')!;
    const type = select.selectedOptions[0]?.dataset['type'];
    const debit = debitLabel(type);
    const credit = creditLabel(type);
    tr.querySelector('[data-debit-label]')!.textContent = debit;
    tr.querySelector('[data-credit-label]')!.textContent = credit;
    // The legend is decoration (see `notchedFieldHtml`); the accessible name
    // has to move with it or a screen reader keeps reading the old wording.
    tr.querySelector('[data-split-debit]')!.setAttribute('aria-label', debit);
    tr.querySelector('[data-split-credit]')!.setAttribute('aria-label', credit);

    refreshWalletCells();
    const account = accounts.find((a) => a.id === select.value);
    const nameEl = tr.querySelector<HTMLElement>('[data-account-name]')!;
    const short = account ? accountShortLabel(accounts, account.id) : undefined;
    const path = short?.full ?? '';
    // Two segments, with the leading one muted and first to be ellipsised —
    // the account's own name is the part worth keeping (accountShortLabel).
    nameEl.innerHTML = short ? accountLabelHtml(short.label) : 'Choose account…';
    nameEl.classList.toggle('wl-acct-btn__name--empty', !account);
    tr.querySelector<HTMLElement>('[data-account-icon]')!.innerHTML =
      account ? inlineAccountIcon(accounts, account.id) : '';
    const btn = tr.querySelector<HTMLElement>('[data-split-account-btn]')!;
    btn.title = account ? path : 'Choose an account';
    btn.setAttribute('aria-label', account ? `Account: ${path}` : 'Choose an account');
  }

  /**
   * Mirrors legacy's own rule (SPEC.md `tx_save`): same currency as the
   * transaction needs no second number at all; a single-currency account
   * whose own currency differs is a *fixed* amount currency, not a choice;
   * only a multi-currency account offers a picker. Re-rendered whenever the
   * row's account changes or the transaction currency itself changes.
   */
  function amountCellHtml(accountId: string, existingAmount?: string, existingCurrency?: string): string {
    const account = bookableAccounts.find((a) => a.id === accountId);
    if (!account) return '<span class="text-secondary small">—</span>';
    if (!needsAccountCurrencyAmount(account, txCurrency())) {
      return '<span class="text-secondary small">same currency</span>';
    }

    // Rounded to *this split's own currency* fraction, never a fixed
    // constant (see money.ts) — this is an amount someone reads directly off
    // that currency's own account statement, not a value computed from a
    // stored exchange rate, so it never carries more precision than the
    // currency itself has. Confirmed against every real imported split: none
    // of the 513 splits in the current dataset carry precision beyond their
    // currency's own `fraction`.
    const splitCurrencyCode = account.isMulticurrency ? (existingCurrency || txCurrency()) : account.currencyCode;
    const fraction = currenciesByCode.get(splitCurrencyCode)?.fraction ?? 2;
    /*
     * A MAGNITUDE, never the signed stored amount.
     *
     * The sign of a split is carried by the debit and credit columns, and the
     * save path reads this field accordingly — it is called `amountAbs` there
     * and combined with `value >= 0` to decide the sign. So a stored `-50000`
     * on a credit leg arrived here as "-50000" in a field declared `min="0"`,
     * and the browser refused to save with "Value must be greater than or
     * equal to 0" on a transaction nobody had touched.
     *
     * Its quieter half was the more dangerous one: had the number ever got
     * past validation, the save would have read -50000 as the magnitude and
     * written +50000 back for a credit — the sign flipped on an imported
     * transaction, silently.
     */
    const displayAmount = existingAmount !== undefined && existingAmount !== ''
      ? Math.abs(Number(existingAmount)).toFixed(fraction) : '';
    const step = stepForFraction(fraction);

    if (account.isMulticurrency) {
      const selected = existingCurrency || txCurrency();
      const options = currencies.map((c) => `<option value="${c.code}" ${c.code === selected ? 'selected' : ''}>${escapeHtml(c.code)}</option>`).join('');
      return `<div class="d-flex gap-1">
        <select class="form-select form-select-sm" data-split-currency style="max-width:5rem">${options}</select>
        <input class="form-control form-control-sm text-end cf-no-spin" data-split-amount type="number" step="${step}" min="0" value="${displayAmount}">
      </div>`;
    }
    // Fixed to the account's own currency — legacy: "whatever the client sent is ignored".
    return `<div class="input-group input-group-sm">
      <span class="input-group-text" data-split-fixed-currency="${account.currencyCode}">${escapeHtml(account.currencyCode)}</span>
      <input class="form-control text-end cf-no-spin" data-split-amount type="number" step="${step}" min="0" value="${displayAmount}">
    </div>`;
  }

  function refreshAmountCell(tr: HTMLTableRowElement, existingAmount?: string, existingCurrency?: string): void {
    const accountId = (tr.querySelector('[data-split-account]') as HTMLSelectElement).value;
    /*
     * What the cell already holds, when the caller does not say.
     *
     * This cell is rebuilt from scratch whenever the row's account or the
     * transaction's currency changes, and most callers have no `existing*`
     * to hand because they are reacting to a change rather than loading a
     * record. They were passing nothing, so the rebuild wrote an empty
     * field: changing the account on a conversion erased the amount that
     * said what it converted to, and changing the second one erased the
     * other. The numbers were still in the DOM a moment before and nothing
     * had asked for them to go.
     *
     * Reading them back makes the default "keep what is there", which is
     * what every one of those callers meant. A caller that really is
     * replacing the values still passes them and still wins; a brand-new
     * row has nothing to read and gets the blank it wants.
     */
    const current = tr.querySelector<HTMLInputElement>('[data-split-amount]')?.value;
    const currentCurrency = tr.querySelector<HTMLSelectElement>('[data-split-currency]')?.value
      ?? tr.querySelector<HTMLElement>('[data-split-fixed-currency]')?.dataset['splitFixedCurrency'];
    const keepCurrency = existingCurrency ?? currentCurrency;

    /*
     * When the cell appears and its currency is the transaction's, fill it in.
     *
     * Changing a leg to a multi-currency account reveals this column with the
     * transaction's own currency already selected — and an empty box. There
     * was nothing to ask: in that currency the account-currency amount *is*
     * the value, which the debit and credit columns are already showing. The
     * blank was a question with one possible answer, and leaving it blank
     * used to book a zero (see `readForm`).
     *
     * Only when they match. A leg in another currency is genuinely unknown
     * here — that figure is half of an exchange rate — and a confident
     * default would be the worst kind of wrong.
     */
    const debit = Number(tr.querySelector<HTMLInputElement>('[data-split-debit]')?.value) || 0;
    const credit = Number(tr.querySelector<HTMLInputElement>('[data-split-credit]')?.value) || 0;
    const fromValue = prefillAccountCurrencyAmount(keepCurrency, txCurrency(), debit - credit);
    const keepAmount = existingAmount
      ?? (current !== undefined && current !== '' ? current : fromValue);
    tr.querySelector('[data-amount-cell]')!.innerHTML = amountCellHtml(accountId, keepAmount, keepCurrency);
    // amountCellHtml has no view of `readOnly` (it's a plain HTML string) — fix up whatever it just built.
    applyFieldReadOnly(tr.querySelector('[data-split-amount]'), readOnly);
    applySelectReadOnly(tr.querySelector('[data-split-currency]'), readOnly);

    const rowId = tr.dataset['rowId']!;
    tr.querySelector<HTMLInputElement>('[data-split-amount]')?.addEventListener('input', (e) => {
      previewSplitField(rowId, 'amount', { t: 'decimal', v: (e.target as HTMLInputElement).value });
      // The debts' leg list and helper read this figure: it is the leg's own.
      refreshMove();
    });
    tr.querySelector<HTMLSelectElement>('[data-split-currency]')?.addEventListener('change', (e) => {
      previewSplitField(rowId, 'splitCurrency', { t: 'string', v: (e.target as HTMLSelectElement).value });
      refreshMove();
    });
  }

  /**
   * A plain two-line transaction balances by definition, so filling one leg
   * fills the other. Only applies while there are exactly two splits, and
   * never overwrites an amount the user typed themselves — a mirrored value
   * is tagged `dataset.auto` so it keeps following its source until the user
   * types into that field directly. Ported verbatim from legacy's
   * `autofillOppositeLeg`.
   */
  function autofillOppositeLeg(srcTr: HTMLTableRowElement): void {
    const trs = [...rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]')];
    if (trs.length !== 2) return;
    const other = trs.find((t) => t !== srcTr);
    if (!other) return;

    const srcDebit = srcTr.querySelector<HTMLInputElement>('[data-split-debit]')!;
    const srcCredit = srcTr.querySelector<HTMLInputElement>('[data-split-credit]')!;
    const otherDebit = other.querySelector<HTMLInputElement>('[data-split-debit]')!;
    const otherCredit = other.querySelector<HTMLInputElement>('[data-split-credit]')!;
    const isManual = (el: HTMLInputElement): boolean => el.value !== '' && el.dataset['auto'] !== '1';
    if (isManual(otherDebit) || isManual(otherCredit)) return;

    const fromDebit = srcDebit.value !== '';
    const val = fromDebit ? srcDebit.value : srcCredit.value || '';
    const target = fromDebit ? otherCredit : otherDebit;
    const spare = fromDebit ? otherDebit : otherCredit;
    spare.value = '';
    delete spare.dataset['auto'];
    if (val === '') { target.value = ''; delete target.dataset['auto']; return; }
    target.value = val;
    target.dataset['auto'] = '1';
  }

  function onSplitAmountTyped(tr: HTMLTableRowElement, input: HTMLInputElement): void {
    delete input.dataset['auto'];
    autofillOppositeLeg(tr);
    updateTotals();
  }

  function addRow(split?: SplitDto): void {
    const rowId = split?.id ?? crypto.randomUUID();
    rows.push({ id: rowId, isNew: !split, ...(split ? { baseVersion: split.version } : {}) });
    const value = split ? Number(split.value) : undefined;
    const txFraction = currenciesByCode.get(txCurrency())?.fraction ?? 2;
    const debitVal = value !== undefined && value > 0 ? Number(split!.value).toFixed(txFraction) : '';
    const creditVal = value !== undefined && value < 0 ? Math.abs(value).toFixed(txFraction) : '';
    const txStep = stepForFraction(txFraction);
    const tr = document.createElement('tr');
    tr.dataset['rowId'] = rowId;
    tr.innerHTML = `
      <td>
        <!-- The select stays as the row's value, hidden: everything that
             reads or writes this row still goes through it, and the button
             only opens the tree and writes back. One source of truth, and no
             rewrite of the save path to introduce a picker. -->
        <select hidden data-split-account>${accountOptionsHtml(split?.accountId ?? '')}</select>
        ${notchedFieldHtml('Account',
          '<button type="button" class="wl-nf__ctl wl-acct-btn" data-split-account-btn aria-label="Account">'
          + '<span data-account-icon></span>'
          + '<span class="wl-acct-btn__name" data-account-name></span>'
          + '<span class="wl-acct-btn__caret cf-chev" aria-hidden="true"></span></button>')}</td>
      <td>${notchedFieldHtml('', `<input class="wl-nf__ctl text-end cf-no-spin" data-split-debit aria-label="Debit" type="number" step="${txStep}" min="0" value="${debitVal}">`, 'data-debit-label')}</td>
      <td>${notchedFieldHtml('', `<input class="wl-nf__ctl text-end cf-no-spin" data-split-credit aria-label="Credit" type="number" step="${txStep}" min="0" value="${creditVal}">`, 'data-credit-label')}</td>
      <td data-amount-cell></td>
      <td data-wallet-cell>${notchedFieldHtml('Wallet',
        `<select class="wl-nf__ctl" data-split-wallet aria-label="Wallet">`
        + walletOptionsHtml(split?.walletId ?? null)
        + '</select>')}<span class="text-secondary small d-inline-block py-2" data-wallet-na hidden>`
        + `<span aria-hidden="true">&mdash;</span>`
        + `<span class="visually-hidden">No wallet: this leg is a category, not money</span></span></td>
      <td>${notchedFieldHtml('Memo', `<input class="wl-nf__ctl" data-split-memo aria-label="Memo" value="${escapeHtml(split?.memo ?? '')}">`)}</td>
      <td><button type="button" class="btn btn-sm btn-outline-danger" data-split-remove>&times;</button></td>`;
    rowsEl.appendChild(tr);
    refreshAmountCell(tr, split?.amount, split?.currencyCode);
    updateSplitLabels(tr);
    applyFieldReadOnly(tr.querySelector('[data-split-memo]'), readOnly);
    applyFieldReadOnly(tr.querySelector('[data-split-debit]'), readOnly);
    applyFieldReadOnly(tr.querySelector('[data-split-credit]'), readOnly);
    applySelectReadOnly(tr.querySelector('[data-split-account]'), readOnly);
    applySelectReadOnly(tr.querySelector('[data-split-wallet]'), readOnly);
    applyButtonReadOnly(tr.querySelector('[data-split-account-btn]'), readOnly);
    tr.querySelector<HTMLElement>('[data-split-remove]')!.hidden = readOnly;

    refreshWalletCells();
    /*
     * A leg's wallet changes nothing about the totals, so it does not reach
     * `updateTotals` — and that is where `refreshMove` hangs. Without this,
     * pointing two legs at two wallets left the "Between wallets" row hidden
     * until something else happened to redraw.
     */
    tr.querySelector<HTMLSelectElement>('[data-split-wallet]')
      ?.addEventListener('change', () => { refreshMove(); });
    const accountSelect = tr.querySelector<HTMLSelectElement>('[data-split-account]')!;
    tr.querySelector<HTMLElement>('[data-split-account-btn]')!.addEventListener('click', (event) => {
      if (readOnly) return;
      openAccountPicker(event.currentTarget as HTMLElement, {
        // The whole chart, not just the bookable leaves: the folders are
        // what makes it a tree. Only a leaf can be chosen — the picker
        // toggles a folder open rather than selecting it — so the hidden
        // select can never be handed an id it does not have an option for.
        accounts, prefs, selectedId: accountSelect.value,
        onPick: (id) => {
          accountSelect.value = id;
          // Through `change`, so the one listener that already knows what a
          // new account means for this row — the amount cell, the wording,
          // the totals, the preview broadcast — does all of it.
          accountSelect.dispatchEvent(new Event('change'));
        },
      });
    });

    const debitInput = tr.querySelector<HTMLInputElement>('[data-split-debit]')!;
    const creditInput = tr.querySelector<HTMLInputElement>('[data-split-credit]')!;
    debitInput.addEventListener('input', () => {
      if (debitInput.value) creditInput.value = '';
      onSplitAmountTyped(tr, debitInput);
      previewSplitField(rowId, 'debit', { t: 'decimal', v: debitInput.value });
      previewSplitField(rowId, 'credit', { t: 'decimal', v: creditInput.value });
    });
    creditInput.addEventListener('input', () => {
      if (creditInput.value) debitInput.value = '';
      onSplitAmountTyped(tr, creditInput);
      previewSplitField(rowId, 'credit', { t: 'decimal', v: creditInput.value });
      previewSplitField(rowId, 'debit', { t: 'decimal', v: debitInput.value });
    });
    tr.querySelector<HTMLInputElement>('[data-split-memo]')?.addEventListener('input', (e) => {
      previewSplitField(rowId, 'memo', { t: 'string', v: (e.target as HTMLInputElement).value });
    });

    tr.querySelector('[data-split-remove]')?.addEventListener('click', () => {
      if (readOnly) return;
      if (split) removedSplitIds.add(split.id);
      tr.remove();
      updateTotals();
    });
    tr.querySelector<HTMLSelectElement>('[data-split-account]')?.addEventListener('change', () => {
      refreshAmountCell(tr);
      updateSplitLabels(tr);
      autoTxCurrency();
      paintTxCurrency();
      updateTotals();
      const sel = tr.querySelector<HTMLSelectElement>('[data-split-account]')!;
      previewSplitField(rowId, 'accountId', sel.value ? { t: 'uuid', v: sel.value } : { t: 'null', v: null });
    });
  }

  function updateTotals(): void {
    let deb = 0;
    let cred = 0;
    rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]').forEach((tr) => {
      const debit = Number((tr.querySelector('[data-split-debit]') as HTMLInputElement).value) || 0;
      const credit = Number((tr.querySelector('[data-split-credit]') as HTMLInputElement).value) || 0;
      deb += debit;
      cred += credit;
    });
    const cur = currenciesByCode.get(txCurrency());
    root.querySelector('#txTotDebit')!.textContent = formatAmount(deb, cur);
    root.querySelector('#txTotCredit')!.textContent = formatAmount(cred, cur);
    const diff = deb - cred;
    const balanced = Math.abs(diff) <= 0.004;
    balanceEl.innerHTML = balanced
      ? `<span class="text-success">${CHECK_ICON} Balanced</span>`
      : `<span class="text-danger">${WARNING_ICON} Out of balance by ${escapeHtml(formatAmount(diff, cur))}</span>`;
    refreshMove();
  }

  /**
   * Show the "Between wallets" rows when the legs ask for them, and keep each
   * row's choices to what the form actually offers.
   *
   * Driven off the form rather than off the loaded transaction, so pointing a
   * leg at a different wallet moves the question with it — and so an ordinary
   * expense never carries a control whose answer would be recorded against
   * two wallets it does not touch.
   *
   * Rebuilt in place rather than re-rendered: these are `<select>`s somebody
   * may be part-way through, and replacing the node under them would lose
   * both the choice and the focus (invariant #5, at form scale).
   */
  function refreshMove(): void {
    const legs = legsForMove();
    const touched = walletsTouched(legs);
    /*
     * Any entry with money in a wallet, not only one whose legs cross two.
     *
     * "Maintenance paid this for the car" is one leg, in one wallet, and is
     * still a debt between two (ADR-0023, amendment of 2026-10-18). The
     * other wallet need not be on the entry at all — the debt is what says
     * it was involved — so both pickers offer every wallet, the ones on the
     * legs first.
     */
    moveBar.hidden = touched.length < 1;
    if (moveBar.hidden) return;

    const others = [...walletsById.keys()]
      .filter((id) => !touched.includes(id))
      .sort((a, b) => walletPathOf(a).localeCompare(walletPathOf(b)));
    const walletChoices = [...touched, ...others].map((id) =>
      ({ value: id, label: walletPathOf(id) || 'unknown wallet' }));
    const legChoices = moveLegChoices();
    for (const row of moveRowEls()) {
      const fromEl = row.querySelector<HTMLSelectElement>('[data-move-from]')!;
      const toEl = row.querySelector<HTMLSelectElement>('[data-move-to]')!;
      fillSelect(fromEl, walletChoices, '— pick one —');
      fillSelect(toEl, walletChoices, '— pick one —');
      /*
       * Only money that is one of the two wallets' own: the leg that left the
       * lender, or the one that reached the borrower. A leg in a third wallet
       * would put a figure on this debt that has nothing to do with it.
       *
       * Either sign. The debt's direction is from → to, never the leg's sign
       * (`transferAmounts` reads it unsigned), so the payment leg out of
       * Maintenance is as good a measure as money landing in the car.
       */
      const legEl = row.querySelector<HTMLSelectElement>('[data-move-leg]')!;
      fillSelect(
        legEl,
        legChoices.filter((c) => legFitsMove(c.walletId, fromEl.value, toEl.value)),
        toEl.value || fromEl.value ? '— pick one —' : '— say which wallets first —',
      );
      const leg = legChoices.find((c) => c.value === legEl.value);
      row.querySelector<HTMLInputElement>('[data-move-amount]')!.placeholder =
        leg ? `all ${leg.magnitude}` : 'all of it';
      paintMoveHelp(row);
    }
  }

  /**
   * Which legs a debt between two wallets may be counted on: one of those two
   * wallets' own, or one in no wallet — the category a payment was booked
   * to, which is where its other currency is. Never a third wallet's: that
   * would put a figure on this debt that has nothing to do with it.
   */
  function legFitsMove(legWallet: string, fromWallet: string, toWallet: string): boolean {
    if (!fromWallet && !toWallet) return false;
    return legWallet === '' || legWallet === fromWallet || legWallet === toWallet;
  }

  /** Under each debt row: where the two wallets stand and what this does (webledgerDebtHelper). */
  function paintMoveHelp(row: HTMLElement): void {
    const box = row.querySelector<HTMLElement>('[data-move-help]');
    if (!box) return;
    const from = row.querySelector<HTMLSelectElement>('[data-move-from]')!.value;
    const to = row.querySelector<HTMLSelectElement>('[data-move-to]')!.value;
    const choices = moveLegChoices();
    const leg = choices.find((c) => c.value === row.querySelector<HTMLSelectElement>('[data-move-leg]')!.value);
    if (!from || !to || from === to || !leg) { box.innerHTML = ''; return; }
    const typed = row.querySelector<HTMLInputElement>('[data-move-amount]')!.value.trim();
    box.innerHTML = debtHelperHtml({
      fromName: walletPathOf(from) || 'unknown wallet',
      toName: walletPathOf(to) || 'unknown wallet',
      // The table is narrow: its cells take the wallet's own name, without its parents.
      fromShort: walletsById.get(from)?.name,
      toShort: walletsById.get(to)?.name,
      kind: normalTransferKind(row.querySelector<HTMLSelectElement>('[data-move-kind]')!.value),
      currencyCode: leg.currencyCode,
      amount: typed === '' ? leg.size : Number(typed),
      legSize: leg.size,
      standing: standingBefore(transferAmounts(datasetLedger(client.dataset)), from, to, {
        txDate: dateInput.value, transactionId: tx?.id,
      }),
      choices: [...new Set(choices.filter((c) => legFitsMove(c.walletId, from, to)).map((c) => c.currencyCode))],
      chip: (n, code, className) => currencyChipHtml(n, code, {
        currency: currenciesByCode.get(code) ?? { fraction: 2, code },
        ...(className ? { className } : {}),
      }),
    });
  }

  /**
   * Each option keeps its value when the list it belongs to is rebuilt, and
   * loses it when the form no longer offers it.
   *
   * The second half is the point: a wallet that is no longer on any leg, or a
   * leg that was deleted, must not stay selected in a row that would then be
   * saved against something the transaction does not contain.
   */
  function fillSelect(
    sel: HTMLSelectElement, choices: ReadonlyArray<{ value: string; label: string }>, empty: string,
  ): void {
    const chosen = choices.some((c) => c.value === sel.value) ? sel.value : '';
    sel.innerHTML = `<option value="">${escapeHtml(empty)}</option>`
      + choices.map((c) => `<option value="${escapeHtml(c.value)}"${c.value === chosen ? ' selected' : ''}>${
        escapeHtml(c.label)}</option>`).join('');
    sel.value = chosen;
  }

  /**
   * The legs, as something to pick from — "which money was the loan".
   *
   * An amount with its currency, as it stands on the form right now, and the
   * account it sits on so two legs of the same size can be told apart. Never
   * a number to type: the amount belongs to the leg, and a second copy here
   * would be free to drift from it (ADR-0023).
   */
  function moveLegChoices(): Array<{
    value: string; label: string; walletId: string; positive: boolean; magnitude: string; size: number;
    currencyCode: string;
  }> {
    const out: Array<{
      value: string; label: string; walletId: string; positive: boolean; magnitude: string; size: number;
      currencyCode: string;
    }> = [];
    for (const tr of rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]')) {
      const accountId = tr.querySelector<HTMLSelectElement>('[data-split-account]')?.value ?? '';
      if (!accountId) continue;
      const debit = Number((tr.querySelector('[data-split-debit]') as HTMLInputElement).value) || 0;
      const credit = Number((tr.querySelector('[data-split-credit]') as HTMLInputElement).value) || 0;
      const value = debit - credit;
      if (value === 0) continue;
      /*
       * In the leg's OWN currency, and its own amount in it. Any leg, not only
       * money: the category leg is where a conversion's other currency is —
       * dollars paid, forints booked to the car — and counting a debt on it
       * is how the debt comes to be owed in forints.
       *
       * The label used to take the debit/credit figure, which is in the
       * ENTRY's currency, and put the leg's currency beside it: the forint
       * leg of a dollar entry read "32,407.42 HUF".
       */
      const ccy = rowLegCurrency(tr) ?? txCurrency();
      const own = ccy === txCurrency() ? Math.abs(value)
        : Number(tr.querySelector<HTMLInputElement>('[data-split-amount]')?.value) || 0;
      if (own === 0) continue;
      const account = accounts.find((a) => a.id === accountId)?.name ?? 'account';
      /*
       * The wallet is part of the label, not decoration.
       *
       * The shape this whole list exists for puts the same amount on the
       * same account twice — out of one wallet and into another — and
       * without the wallet the picker offered two identical lines and no way
       * to tell which was which. Which is the question being asked.
       */
      const wallet = isMoneyAccount(accountId)
        ? tr.querySelector<HTMLSelectElement>('[data-split-wallet]')?.value ?? '' : '';
      const whose = wallet ? ` · ${walletPathOf(wallet) || 'unknown wallet'}` : '';
      out.push({
        value: tr.dataset['rowId']!,
        label: `${account} · ${formatAmount(value < 0 ? -own : own, currenciesByCode.get(ccy))} ${ccy}${whose}`,
        walletId: wallet,
        positive: value > 0,
        magnitude: formatAmount(own, currenciesByCode.get(ccy)),
        size: own,
        currencyCode: ccy,
      });
    }
    return out;
  }

  function moveRowEls(): HTMLElement[] {
    return [...moveRowsEl.querySelectorAll<HTMLElement>('[data-move-id]')];
  }

  /** What the rows currently say, in the shape the save sends. */
  function moveRowValues(): WalletMove[] {
    return moveRowEls().map((row) => ({
      id: row.dataset['moveId']!,
      kind: row.querySelector<HTMLSelectElement>('[data-move-kind]')!.value,
      fromWalletId: row.querySelector<HTMLSelectElement>('[data-move-from]')!.value,
      toWalletId: row.querySelector<HTMLSelectElement>('[data-move-to]')!.value,
      amountSplitId: row.querySelector<HTMLSelectElement>('[data-move-leg]')!.value,
      amount: row.querySelector<HTMLInputElement>('[data-move-amount]')!.value.trim() || null,
    }));
  }

  /**
   * One row. Its id is the sidecar record's own — loaded for a row that
   * exists, minted here for a new one (invariant #8) — so a save reconciles
   * against what is there instead of replacing the lot every time.
   */
  function addMoveRow(seed?: WalletMove): void {
    const loaded = seed ?? {
      id: crypto.randomUUID(), kind: 'loan', fromWalletId: '', toWalletId: '', amountSplitId: '', amount: null,
    };
    // A stored "repay" is a loan the other way round, and is offered as one:
    // the direction says which (`normalTransferKind`).
    const move = { ...loaded, kind: loaded.kind === 'repay' ? 'loan' : loaded.kind };
    const row = document.createElement('div');
    row.className = 'wl-xfer__row';
    row.dataset['moveId'] = move.id;
    const kindOption = (value: string, label: string): string =>
      `<option value="${value}"${value === move.kind ? ' selected' : ''}>${label}</option>`;
    row.innerHTML = `
      <select class="form-select form-select-sm w-auto" data-move-kind
        aria-label="What this move between wallets is">
        ${kindOption('loan', 'Loan / payback')}
        ${kindOption('grant', 'Free grant')}
      </select>
      <span class="small text-secondary">from</span>
      <select class="form-select form-select-sm w-auto" data-move-from
        aria-label="Which wallet the money came out of"></select>
      <span class="small text-secondary">to</span>
      <select class="form-select form-select-sm w-auto" data-move-to
        aria-label="Which wallet the money went into"></select>
      <span class="small text-secondary">of</span>
      <select class="form-select form-select-sm w-auto" data-move-leg
        aria-label="Which leg of this entry the move was"></select>
      <input class="form-control form-control-sm w-auto text-end cf-no-spin" type="number" min="0" step="any"
        data-move-amount size="10" aria-label="How much of that leg — leave empty for all of it">
      <button type="button" class="btn btn-sm btn-outline-danger" data-move-remove
        aria-label="Remove this move">\u00d7</button>
      <div class="w-100" data-move-help></div>`;
    moveRowsEl.appendChild(row);

    // Options first, then the loaded values: `fillSelect` keeps a value only
    // when the list already offers it.
    refreshMove();
    const set = (what: string, value: string): void => {
      const sel = row.querySelector<HTMLSelectElement>(`[data-move-${what}]`)!;
      if ([...sel.options].some((o) => o.value === value)) sel.value = value;
    };
    set('from', move.fromWalletId);
    set('to', move.toWalletId);
    // Again, now that both are set: the leg list is the money of THOSE two
    // wallets, so until they are chosen there is nothing to offer.
    refreshMove();
    /*
     * A row written before there was a leg to name gets the old rule's
     * answer put in front of it: the first leg that landed in the
     * destination wallet. Not silently — it is sitting in the picker where
     * it can be read and changed — and saving the entry writes it down, so
     * the guess stops being a guess the first time somebody touches it.
     */
    set('leg', move.amountSplitId || guessMoveLeg(move.toWalletId));
    // The part, when the row is for part of the leg. Number-formatted to the
    // column's scale, so a 10000.0000 does not read as more than it is.
    row.querySelector<HTMLInputElement>('[data-move-amount]')!.value =
      move.amount ? String(Number(move.amount)) : '';
    refreshMove();

    for (const what of ['to', 'from', 'leg']) {
      row.querySelector<HTMLSelectElement>(`[data-move-${what}]`)!
        .addEventListener('change', () => { refreshMove(); });
    }
    row.querySelector<HTMLSelectElement>('[data-move-kind]')!.addEventListener('change', () => { paintMoveHelp(row); });
    row.querySelector<HTMLInputElement>('[data-move-amount]')!.addEventListener('input', () => { paintMoveHelp(row); });
    // The helper's offers: settle exactly, or count it in the currency owed.
    row.querySelector<HTMLElement>('[data-move-help]')!.addEventListener('click', (event) => {
      if (readOnly) return;
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-debt-settle],[data-debt-ccy]');
      if (!target) return;
      const amountEl = row.querySelector<HTMLInputElement>('[data-move-amount]')!;
      const settle = target.dataset['debtSettle'];
      const code = target.dataset['debtCcy'];
      if (settle !== undefined) {
        amountEl.value = String(Number(settle));
      } else if (code !== undefined) {
        const from = row.querySelector<HTMLSelectElement>('[data-move-from]')!.value;
        const to = row.querySelector<HTMLSelectElement>('[data-move-to]')!.value;
        const leg = moveLegChoices().find((c) => c.currencyCode === code && legFitsMove(c.walletId, from, to));
        if (!leg) return;
        row.querySelector<HTMLSelectElement>('[data-move-leg]')!.value = leg.value;
        amountEl.value = '';
      }
      refreshMove();
    });
    row.querySelector<HTMLButtonElement>('[data-move-remove]')!
      .addEventListener('click', () => { row.remove(); refreshMove(); });
  }

  /** The old rule, used only to fill a picker a human then looks at. */
  function guessMoveLeg(toWalletId: string): string {
    if (!toWalletId) return '';
    for (const tr of rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]')) {
      const accountId = tr.querySelector<HTMLSelectElement>('[data-split-account]')?.value ?? '';
      if (!isMoneyAccount(accountId)) continue;
      if (tr.querySelector<HTMLSelectElement>('[data-split-wallet]')?.value !== toWalletId) continue;
      const debit = Number((tr.querySelector('[data-split-debit]') as HTMLInputElement).value) || 0;
      const credit = Number((tr.querySelector('[data-split-credit]') as HTMLInputElement).value) || 0;
      if (debit - credit > 0) return tr.dataset['rowId']!;
    }
    return '';
  }

  function legsForMove(): Array<{ walletId: string | null; value: number }> {
    return [...rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]')].map((tr) => {
      const accountId = tr.querySelector<HTMLSelectElement>('[data-split-account]')?.value ?? '';
      const debit = Number((tr.querySelector('[data-split-debit]') as HTMLInputElement).value) || 0;
      const credit = Number((tr.querySelector('[data-split-credit]') as HTMLInputElement).value) || 0;
      const walletSelect = tr.querySelector<HTMLSelectElement>('[data-split-wallet]');
      return {
        walletId: isMoneyAccount(accountId) && walletSelect?.value ? walletSelect.value : null,
        value: debit - credit,
      };
    });
  }

  /** Rebuilds the whole form's content from a fetched transaction — used for the initial load, every live refresh while read-only, and the moment a reclaim succeeds. Does not touch read-only/editable state itself; call `setFormReadOnly` first. */
  function populate(freshTx: TransactionDto): void {
    tx = freshTx;
    /*
     * The existing kind, from the replica rather than from the response: the
     * sidecar is its own record and the transaction route does not carry it.
     * `walletTransfers()` is what the register reads for the same label.
     */
    loadedMoves = freshTx.id
      ? datasetLedger(client.dataset).walletTransfers()
        .filter((wt) => wt.transactionId === freshTx.id)
        .map((wt) => ({
          id: wt.id,
          kind: wt.kind,
          fromWalletId: wt.fromWalletId,
          toWalletId: wt.toWalletId,
          amountSplitId: wt.amountSplitId ?? '',
          amount: wt.amount ?? null,
        }))
      : [];
    moveRowsEl.innerHTML = '';
    baseVersion = freshTx.version;
    titleEl.textContent = freshTx.description || 'Transaction';
    dateInput.value = freshTx.txDate;
    referenceInput.value = freshTx.reference;
    descriptionInput.value = freshTx.description;
    currencySelect.value = freshTx.currencyCode;
    rows.length = 0;
    rowsEl.innerHTML = '';
    removedSplitIds.clear();
    root.querySelectorAll<HTMLElement>('.txCurLabel').forEach((el) => { el.textContent = `(${txCurrency()})`; });
    freshTx.splits.forEach((s) => addRow(s));
    paintTxCurrency();
    // After the legs, because each row's leg picker is built from them.
    updateTotals();
    for (const move of loadedMoves) addMoveRow(move);
  }

  if (existing) {
    titleEl.textContent = existing.description || 'Transaction';
    // Presence-only join (no lease of its own yet) — lets a denied claim
    // resolve who actually holds it (via `session.presence`, reactively as
    // it arrives) and lets `leaseFreed`/`recordEvent` reach this tab at all,
    // both needed below regardless of how the claim itself turns out.
    await session.openRecord('transaction', txId);

    // Read before claiming: the record's own state is what the form shows
    // whichever way the claim goes, and offline it is all there is.
    const read = await readCached<{ transaction: TransactionDto }>(
      docCache, `transaction:${existing.id}`, `/apps/webledger/transactions/${existing.id}`, token);
    if (!read.ok && read.status !== 0) {
      session.closeRecord('transaction', txId);
      // This returns before `show()`, so there is no modal to put the message
      // in — it belongs on the page the user is still looking at, where the
      // row they clicked otherwise just sat there doing nothing.
      notify('Could not load this transaction.');
      showPageNotice(root, 'Could not load this transaction.');
      return;
    }
    // Unreachable with nothing cached for this specific record — but the list
    // response we are already holding carries the whole transaction, splits
    // included. Using it means *every* listed transaction opens offline, not
    // only ones that happen to have been opened on this device before. It is
    // the same data the per-record endpoint returns, minus the live lock and
    // owner fields, which are meaningless offline anyway.
    const loaded = read.ok ? read : undefined;
    const fresh = loaded ? loaded.data.transaction : existing;
    if (!loaded) offlineSince = docCache.get('transactions')?.cachedAt ?? new Date().toISOString();

    /**
     * An edit of this record already sitting in the outbox, unsent.
     *
     * It has to win over `fresh` offline, and that is not a nicety: `fresh`
     * comes from whichever *server* copy we can reach — the per-record cache
     * if this transaction was ever opened online, the list entry otherwise —
     * and neither knows anything about work the user has done since. Opening
     * an already-edited transaction offline therefore showed the pre-edit
     * values and silently dropped the queued version on the next save.
     */
    const queuedDocs = await outbox.allDocuments();
    const queuedEdit = queuedUpdateFor(queuedDocs, txId);
    /**
     * A draft somebody interrupted, from this session or an earlier one — it
     * is durable precisely so that closing the tab in frustration does not
     * finish the job the person who took the record started. Loaded here, so
     * reopening the transaction still offers it.
     */
    heldDraft = heldFor(queuedDocs, txId);
    if (heldDraft?.held) {
      const takenBy = heldDraft.held.takenBy;
      const name = heldDraft.held.takenByName;
      stolenBy = personFor(takenBy) ?? {
        id: takenBy, name,
        initials: name.split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase() || '?',
        colorHex: '#8a8f98',
      };
    }
    hold = new RecordHold({
      client: { leaseClient, transport, session, userId },
      entity: 'transaction', recordId: txId,
      onView: (view) => { void onHoldView(view); },
      onTaken: (by) => keepDraftTakenBy(by),
      onFreed: async () => { if (formReady) await pickUpFreed(); else freedBeforeReady = true; },
    });

    if (!loaded || loaded.stale) {
      // Offline, and **editable** (ADR-0011). The save queues into the
      // outbox with the `baseVersion` this copy carried; if the record has
      // moved on by the time it replays, the whole group is held as a
      // pending revision to apply or discard rather than silently overwriting
      // whoever got there first. A hold this tab already had stays its own
      // meanwhile, and is resumed when the connection is back.
      offlineSince = loaded?.cachedAt ?? offlineSince;
      setFormReadOnly(false);
      // The queued edit carries its own `baseVersion`, so re-saving keeps
      // compare-and-swapping against the base the first edit was made on and
      // simply replaces the outbox entry (`enqueueDocument` collapses by
      // record) rather than stacking a second one.
      const chosen = documentToEdit(fresh, queuedEdit);
      populate(chosen.tx);
      holdView = await hold.open(fresh, true);
      renderOfflineEditNotice(chosen.fromOutbox);
    } else {
      // Claim — or resume this tab's own hold, after a reload of a discarded
      // tab. Refused when anybody holds it, connected or not: a hold no
      // longer lapses (ADR-0003, 2026-10-22), so somebody's unfinished edit
      // is never walked into silently, and Take over is always there.
      setFormReadOnly(true);
      populate(fresh);
      holdView = await hold.open(fresh, false);
      if (holdView.kind === 'mine') {
        setFormReadOnly(false);
      } else if (holdView.kind === 'offline') {
        offlineSince = new Date().toISOString();
        setFormReadOnly(false);
        renderOfflineEditNotice(false);
      } else {
        updateNotice();
      }
    }
    // After whichever branch populated the form, so the markers sit beside
    // the values they disagree with rather than being wiped by the populate
    // that follows them.
    if (heldDraft) paintHeldMarkers();
  } else {
    dateInput.value = todayIso();
    referenceInput.value = '';
    descriptionInput.value = '';
    const lastCurrency = prefs.get(PREF.lastTxCurrency);
    currencySelect.value = lastCurrency && currenciesByCode.has(lastCurrency) ? lastCurrency : sortedCurrencyCodes[0] ?? '';
    titleEl.textContent = 'New transaction';
    setFormReadOnly(false);
    root.querySelectorAll<HTMLElement>('.txCurLabel').forEach((el) => { el.textContent = `(${txCurrency()})`; });
    // Whole-form default for a brand-new transaction: the last account used
    // as a debit leg, and the last used as a credit leg (kept distinct),
    // mirroring legacy's `openTxModal` defaulting — see `rememberTxDefaults`.
    const lastDebitAccountId = prefs.get(PREF.lastTxDebitAccount);
    let lastCreditAccountId = prefs.get(PREF.lastTxCreditAccount);
    if (lastCreditAccountId && lastCreditAccountId === lastDebitAccountId) lastCreditAccountId = undefined;
    addRow();
    addRow();
    const [row1, row2] = [...rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]')];
    const applyDefault = (tr: HTMLTableRowElement | undefined, accountId: string | undefined): void => {
      if (!tr || !accountId || !bookableAccounts.some((a) => a.id === accountId)) return;
      (tr.querySelector('[data-split-account]') as HTMLSelectElement).value = accountId;
      refreshAmountCell(tr);
      updateSplitLabels(tr);
    };
    applyDefault(row1, lastDebitAccountId);
    applyDefault(row2, lastCreditAccountId);
    txCurrencyChosen = false;
    autoTxCurrency();
    paintTxCurrency();
    updateTotals();
  }

  /** The record as the server has it now — to repaint a form that is read-only or was just regained. */
  async function fetchCurrent(): Promise<TransactionDto | undefined> {
    const res = await apiFetch(`/apps/webledger/transactions/${txId}`, { token }).catch(() => undefined);
    return res?.ok ? ((await res.json()) as { transaction: TransactionDto }).transaction : undefined;
  }

  const personOf = (h: Holder): PresenceUser | undefined => h.person ?? personFor(h.userId);

  /**
   * Taken off this tab (a take-over, or one that happened while it was
   * offline). Snapshot first, flip second: once the form goes read-only the
   * new holder's live preview starts landing in these very controls, and
   * anything not captured by then is gone for good (ADR-0003, 2026-09-12).
   */
  async function keepDraftTakenBy(by: Holder): Promise<void> {
    const taker = personOf(by);
    stolenBy = by.userId === userId && taker ? { ...taker, name: 'Your other tab' } : taker;
    tookFrom = undefined;
    await holdDraft(stolenBy);
    setFormReadOnly(true);
    // Repaint with the record as it stands — the one moment overwriting the
    // form is right: the draft is captured, and without this every marker
    // would compare the draft against itself and find no difference.
    const current = await fetchCurrent();
    if (current) populate(current);
    updateNotice();
    // Nothing to decide about means nothing to hold.
    if (paintHeldMarkers() === 0) await releaseHeldDraft();
    const name = stolenBy?.name ?? 'Someone';
    notify(heldDraft
      ? `${name} took this transaction over. Your unsaved changes are kept.`
      : `${name} took this transaction over.`);
  }

  /**
   * The record came free while this form watched it, and this tab has it
   * now. Reload what the last holder saved; a kept draft of ours is offered
   * beside it rather than reinstated behind anybody's back.
   */
  async function pickUpFreed(): Promise<void> {
    // A past version on screen stays on screen: `backToCurrent` is what
    // leaves that state, and it sets the form editable itself.
    if (timeline.viewing() !== undefined) return;
    const current = await fetchCurrent();
    setFormReadOnly(false);
    if (current) populate(current);
    if (heldDraft) {
      paintHeldMarkers();
      notify('You have this transaction back. Your held changes are offered beside the current values — keep the ones you want, then save.');
      return;
    }
    notify('This transaction is editable again.');
  }

  /**
   * A discarded tab's unsaved values, back in the form. Ours to put back when
   * the entry is still this tab's (or new, or offline); kept beside the
   * current values as a held draft when somebody else has it by now — the
   * same as if they had taken it over while we looked (2026-09-12).
   */
  async function applyRestoredDraft(draft: QueuedTransactionPayload): Promise<void> {
    if (!readOnly) {
      populate({ ...queuedPayloadToDto(draft), ...(existing ? {} : { version: 0 }) });
      notify('Your unsaved changes are back, as you left them.');
      return;
    }
    if (holdView.kind !== 'theirs') return;
    const by = personOf(holdView.holder);
    stolenBy = by;
    heldDraft = await outbox.enqueueDocument({
      entity: 'transaction', recordId: txId, op: 'update', payload: draft,
      clock: session.nextClock(),
      held: {
        takenBy: holdView.holder.userId, takenByName: by?.name ?? 'Someone',
        at: new Date().toISOString(),
      },
    });
    if (paintHeldMarkers() === 0) await releaseHeldDraft();
    else notify('Your unsaved changes are kept — somebody else has this transaction now.');
  }

  /** Take over, or take back: always offered, because losing it is survivable (2026-09-12). */
  async function takeOverNow(): Promise<void> {
    if (!hold) return;
    clearError();
    const before = holdView;
    const outcome = await hold.takeOver();
    if (!outcome.ok) { updateNotice(); return; }
    const current = await fetchCurrent();
    if (!current) {
      hold.release();
      showError('Could not load this transaction.');
      return;
    }
    setFormReadOnly(false);
    populate(current);
    if (heldDraft) {
      paintHeldMarkers();
      notify('You have this transaction back. Your held changes are offered beside the current values — keep the ones you want, then save.');
      return;
    }
    const from = before.kind === 'theirs' && !before.sameUser ? personOf(before.holder) : undefined;
    if (from) {
      tookFrom = from;
      renderBanner(0);
      notify(`You took this transaction over from ${from.name}. They keep their unsaved changes and can take it back.`);
      return;
    }
    notify('This transaction is editable again.');
  }

  /**
   * The hold changed: somebody else has it, the connection went, it was
   * refused. Taken off this tab arrives through `keepDraftTakenBy` first.
   */
  async function onHoldView(view: HoldView): Promise<void> {
    holdView = view;
    if (view.kind === 'offline') {
      if (!readOnly) renderOfflineEditNotice(false);
      return;
    }
    if (view.kind === 'mine') {
      if (!readOnly && offlineSince === undefined) noticeEl.hidden = true;
      return;
    }
    if (view.kind === 'theirs' && !readOnly && !heldDraft) {
      // Editable here, and somebody else turns out to have it — the connection
      // came back and they had taken it meanwhile, or it was theirs before
      // this form went offline. Whatever was typed here is kept, exactly as
      // for a take-over, before the form is handed to them.
      await keepDraftTakenBy(view.holder);
      return;
    }
    if (view.kind === 'refused' && !readOnly) {
      setFormReadOnly(true);
      showError(view.reason === 'immutable'
        ? 'This transaction can no longer be edited.'
        : 'You do not have permission to edit this transaction.');
    }
    updateNotice();
  }

  /**
   * Delegated, because every one of these buttons lives in markup that is
   * rebuilt from scratch as state changes — the notice on each presence
   * change, the banner and the markers on each repaint — so a handler bound
   * to a button would not survive its first update.
   *
   * On the modal, not on the notice: the held-draft banner and the per-field
   * markers are elsewhere in the form, and with the listener scoped to the
   * notice their buttons were inert. Removed on close, which the previous
   * version did not do — the modal element outlives the editor, so every
   * reopen added another listener holding another closure over stale state,
   * and Take over eventually fired several times from several different
   * transactions' worth of variables.
   */
  const modalClickHandler = (e: MouseEvent): void => {
    const el = e.target as HTMLElement;
    if (el.closest('#txTakeOverBtn') || el.closest('#txTakeBackBtn')) { void takeOverNow(); return; }
    const keepOne = el.closest<HTMLElement>('[data-keep-mine]');
    if (keepOne) { applyHeldValue(keepOne.dataset['keepMine']!); return; }
    if (el.closest('#txKeepAllMineBtn')) {
      // Every difference at once, in a fixed order so a split you added and a
      // split you deleted cannot depend on which marker you clicked first.
      const mine = heldDraft?.payload as QueuedTransactionPayload | undefined;
      if (mine) {
        for (const diff of heldDifferences(mine, readForm().body, accountLabel)) applyHeldValue(diff.where);
      }
      return;
    }
    if (el.closest('#txDiscardMineBtn')) {
      void releaseHeldDraft();
      notify('Your held changes were discarded.');
      return;
    }
  };
  modalEl.addEventListener('click', modalClickHandler);

  let unsubscribeTransport: (() => void) | undefined;
  let unsubscribePresence: (() => void) | undefined;
  let unsubscribeReconnect: (() => void) | undefined;
  if (existing) {
    /*
     * The hold itself — taken, freed, taken off this tab, the connection
     * going and coming back — is `hold`'s (webledgerHold.ts). What stays here
     * is the form: a read-only form keeps showing the record live, and a form
     * that was showing a cached copy re-reads it when the connection is back.
     */
    unsubscribeReconnect = transport.onStateChange((state) => {
      if (state === 'open' && offlineSince !== undefined) void recoverFromOffline();
    });

    /**
     * The connection came back while this form was showing a cached copy.
     * Only a form nobody could have typed into is repainted — offline editing
     * is allowed, and repopulating an editable form would throw away work in
     * progress (invariant #5). If the record moved on meanwhile, the save
     * says so (stale base → pending revision) instead. Whether this tab may
     * edit it is `hold`'s answer, arriving through `onHoldView`.
     */
    async function recoverFromOffline(): Promise<void> {
      const read = await readCached<{ transaction: TransactionDto }>(
        docCache, `transaction:${txId}`, `/apps/webledger/transactions/${txId}`, token);
      if (!read.ok || read.stale) return; // still unreachable; stay as we are
      offlineSince = undefined;
      if (readOnly) {
        populate(read.data.transaction);
        updateNotice();
      } else if (holdView.kind === 'mine') {
        noticeEl.hidden = true;
        notify('Back online — your changes are not saved yet, and this transaction is reserved for you.');
      }
    }
    unsubscribeTransport = transport.onMessage((msg) => {
      // A read-only form shows the record live: somebody's save repaints it.
      if (msg.t === 'recordEvent' && msg.entity === 'transaction' && msg.recordId === txId && readOnly
        && timeline.viewing() === undefined) {
        void (async () => {
          const current = await fetchCurrent();
          if (!current) return;
          populate(current);
          if (heldDraft) paintHeldMarkers();
          updateNotice();
        })();
      }
      if (msg.t === 'preview') applyLivePreview(msg);
    });
    unsubscribePresence = session.subscribe(updateNotice);
  }

  // ------------------------------------------------------------- history

  /**
   * The change timeline, as a slide-over beside the open form.
   *
   * Cached like everything else, and primed for the whole ledger in the
   * background (`primeHistory`) — a measured 2.6 KB per transaction makes
   * carrying all of it cheaper than deciding which parts to keep. The cached
   * copy is tagged with the transaction version it was fetched at, so opening
   * a transaction whose history is already current costs no request at all.
   */
  /*
   * The host page is supposed to provide this (see the note at the top of
   * this file), but the `!` that used to be here asserted rather than
   * checked — so a page that forgot it compiled cleanly and then failed at
   * runtime, inside a lease-state callback, on the *second* open only. That
   * is a long way from the actual mistake.
   *
   * Create it instead, and say so. The element is an empty div on every
   * other page, so making one is faithful rather than a guess.
   */
  const timelineEl = root.querySelector<HTMLElement>('#wlTimeline') ?? (() => {
    const made = document.createElement('div');
    made.id = 'wlTimeline';
    root.append(made);
    console.warn('[webledger] the page hosting the transaction editor has no <div id="wlTimeline">; added one.');
    return made;
  })();
  const historyBtn = root.querySelector<HTMLButtonElement>('#txHistoryBtn')!;

  function historyContext(history: TransactionHistory | undefined): HistoryContext {
    return {
      accountName: (id) => accounts.find((a) => a.id === id)?.name ?? id.slice(0, 8),
      // The vocabularies this form already has loaded, so history can name
      // what it refers to instead of printing an id at the reader.
      tagName: (id) => allTags.find((t) => t.id === id)?.name,
      walletName: (id) => (walletsById.has(id) ? walletPathOf(id) : undefined),
      currency: (code) => currencies.find((c) => c.code === code),
      txCurrencyCode: txCurrency(),
      splitNumber: splitNumbering(history?.commits ?? []),
      tagOfLink: tagLinkTargets(history?.commits ?? []),
    };
  }

  /**
   * The timeline lives in `webledgerHistoryPanel` now, because the short form
   * on the wallets page needs the same thing and the parts that matter —
   * cache-or-ask, refusing a restore without a lease, never building a
   * compare-and-swap from a past version's split versions — are exactly the
   * parts it would be dangerous to have two of.
   *
   * What stays here is what is particular to this form: it can show any
   * version, because it has as many split rows as the version needs.
   */
  const timeline = createHistoryPanel({
    txId, token, client, mount: timelineEl, toggleBtn: historyBtn,
    fullPageHref: appHref(`/webledger/transactions/${txId}/history`),
    context: (history) => historyContext(history),
    baseVersion: () => baseVersion,
    leaseId: () => heldLeaseId(),
    canEdit: () => !readOnly || readOnlyForViewing,
    currentSplits: () => rows.filter((r) => !r.isNew).map((r) => ({ id: r.id, version: r.baseVersion ?? 0 })),
    showAsOf: (snapshot) => {
      /*
       * A history value as the text this form shows.
       *
       * `String()` on `FieldValue['v']` stopped being safe when the `json`
       * type arrived (ADR-0026): its `v` is `unknown`, so a value never
       * meant to be text would render as "[object Object]" rather than be
       * refused. Everything here is a scalar; anything else takes the
       * fallback.
       */
      const text = (value: { v: unknown } | undefined, fallback: string): string => {
        const raw = value?.v;
        if (typeof raw === 'string') return raw;
        if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
        return fallback;
      };
      populate({
        id: txId,
        txDate: text(snapshot.fields['txDate'], ''),
        description: text(snapshot.fields['description'], ''),
        reference: text(snapshot.fields['reference'], ''),
        currencyCode: text(snapshot.fields['currencyCode'], txCurrency()),
        version: baseVersion,
        splits: [...snapshot.splits.entries()].map(([id, f]) => ({
          id, transactionId: txId,
          accountId: text(f['accountId'], ''),
          memo: text(f['memo'], ''),
          value: text(f['value'], '0'),
          amount: text(f['amount'], '0'),
          currencyCode: text(f['currencyCode'], ''),
          version: 0,
        })),
      });
      // Read-only on purpose: this is a past state, and editing it in place
      // would silently turn "looking" into "restoring".
      readOnlyForViewing = true;
      setFormReadOnly(true);
      return true;
    },
    backToCurrent: async () => {
      const read = await readCached<{ transaction: TransactionDto }>(
        docCache, `transaction:${txId}`, `/apps/webledger/transactions/${txId}`, token);
      if (read.ok) populate(read.data.transaction);
      readOnlyForViewing = false;
      setFormReadOnly(heldLeaseId() === undefined);
      if (heldLeaseId() !== undefined) noticeEl.hidden = true;
      else updateNotice();
    },
    notice: (html) => {
      if (html === null) { noticeEl.hidden = true; return null; }
      noticeEl.hidden = false;
      noticeEl.innerHTML = html;
      return noticeEl;
    },
    showError, notify,
    onRestored: () => onSaved(txId),
    escapeHtml, presenceDotHtml, timeAgo,
  });

  if (existing) {
    historyBtn.hidden = false;
    historyBtn.addEventListener('click', () => { void timeline.toggle(); });
    if (opts.openHistory) void timeline.open();

    const simpleBtn = root.querySelector<HTMLButtonElement>('#txSimpleBtn')!;
    const handBack = opts.simpleEditor;
    // Never on a transaction that does not exist yet: there is nothing for
    // the short form to open, and Cancel is what leaves an unsaved draft.
    simpleBtn.hidden = handBack === undefined || !existing;
    if (handBack !== undefined && existing) {
      simpleBtn.addEventListener('click', () => {
        const id = txId;
        // Closed first, and the lease goes with it — the short form claims
        // the same record lease the moment it opens, and would otherwise
        // read the record as held by this very tab and refuse to open at
        // all. `hidden.bs.modal` is where this editor releases, so the hand
        // back waits for it rather than racing it.
        modalEl.addEventListener('hidden.bs.modal', () => { handBack(id); }, { once: true });
        bsModal(modalEl)?.hide();
      });
    }
  }

  const addSplitHandler = (): void => { if (!readOnly) addRow(); };
  moveAddEl.addEventListener('click', () => { if (!readOnly) addMoveRow(); });
  root.querySelector('#txAddSplit')?.addEventListener('click', addSplitHandler);

  // The transaction currency changes what "same currency" means for every
  // row at once — re-render every amount cell, not just the one being typed
  // into. Existing rows lose their per-row amount/currency override here
  // deliberately: changing the whole transaction's currency is rare and
  // already re-opens the cross-currency question for every leg.
  // A declaration, not a `const`: `autoTxCurrency` calls it while a new
  // entry is being set up, which runs above this line.
  function currencyChangeHandler(): void {
    root.querySelectorAll<HTMLElement>('.txCurLabel').forEach((el) => { el.textContent = `(${txCurrency()})`; });
    const txStep = stepForFraction(currenciesByCode.get(txCurrency())?.fraction ?? 2);
    rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]').forEach((tr) => {
      refreshAmountCell(tr);
      tr.querySelector('[data-split-debit]')?.setAttribute('step', txStep);
      tr.querySelector('[data-split-credit]')?.setAttribute('step', txStep);
    });
    updateTotals();
    paintTxCurrency();
    previewTransactionField('currencyCode', { t: 'string', v: txCurrency() });
  }
  const currencyPickedHandler = (): void => {
    txCurrencyChosen = true;
    currencyChangeHandler();
  };
  currencySelect.addEventListener('change', currencyPickedHandler);

  const submitHandler = (event: SubmitEvent): void => {
    event.preventDefault();
    if (readOnly) return; // defensive — the Save button is hidden, but a readonly text input can still submit on Enter
    void save();
  };
  /*
   * Stepping to the next or previous transaction of the list behind this
   * form.
   *
   * What the form holds is compared against what it was given, so "unsaved"
   * means the user changed something rather than merely opened it — asking
   * "save your changes?" of somebody who typed nothing is how a prompt
   * teaches people to dismiss prompts.
   *
   * Read off the controls, never off the body a save would send: a body is
   * built for sending, so it mints ids for what it is about to create, and
   * two built a second apart from an untouched form differ in every one.
   */
  let openedWith = formSnapshot(form);

  function hasUnsavedChanges(): boolean {
    if (readOnly || timeline.viewing() !== undefined) return false;
    return formSnapshot(form) !== openedWith;
  }

  /*
   * Which way round the two directions are.
   *
   * `idOf` counts rows as the list draws them, and the log draws newest
   * first — so one step DOWN it is one step back in time. Naming the two
   * ends in the vocabulary of the ledger rather than of the array is what
   * keeps the buttons, their arrows and the swipes agreeing with each
   * other; the one place that has to know the list is sorted that way is
   * this pair of constants.
   */
  const OLDER = 1;
  const YOUNGER = -1;

  function paintNav(): void {
    const neighbours = opts.neighbours;
    navEl.hidden = neighbours === undefined || !existing;
    if (navEl.hidden || !neighbours) return;
    prevBtn.disabled = neighbours.idOf(txId, OLDER) === undefined;
    nextBtn.disabled = neighbours.idOf(txId, YOUNGER) === undefined;
  }

  async function step(delta: -1 | 1): Promise<void> {
    const neighbours = opts.neighbours;
    const goTo = neighbours?.idOf(txId, delta);
    if (!neighbours || goTo === undefined) return;

    if (hasUnsavedChanges()) {
      /*
       * Two answers, and neither of them discards anything. Leaving on
       * purpose is what Cancel already means, and a third button here would
       * be the only place in this app where walking away throws work out.
       */
      const go = await askConfirm({
        title: 'Save your changes first?',
        message: 'This transaction has changes that have not been saved. '
          + 'Cancel stays here and leaves them on the form.',
        confirm: 'Save and continue',
        cancel: 'Stay here',
      });
      if (!go) return;
      await save();
      // `save` leaves the error on screen when it could not go through —
      // stepping away would take the reason with it.
      if (!errorEl.hidden) return;
    }
    neighbours.go(goTo);
  }

  // Named, so they can be taken off again on hide — this modal's elements
  // are static and every open would otherwise add another pair, which on a
  // form whose whole job is to step would compound fast.
  const prevHandler = (): void => { void step(OLDER); };
  const nextHandler = (): void => { void step(YOUNGER); };
  prevBtn.addEventListener('click', prevHandler);
  nextBtn.addEventListener('click', nextHandler);
  // The same two moves under a thumb. Right for the younger entry, left for
  // the older — and only when there is a list to move through at all, which
  // is what `paintNav` has just decided.
  const unswipe = wireSwipeNav(modalEl, {
    onRight: () => { if (!nextBtn.disabled && !navEl.hidden) void step(YOUNGER); },
    onLeft: () => { if (!prevBtn.disabled && !navEl.hidden) void step(OLDER); },
  });
  paintNav();

  form.addEventListener('submit', submitHandler);

  /**
   * The form, read out as the body a save would send.
   *
   * Extracted from `save` because three separate things need exactly this
   * now: saving, queueing offline, and snapshotting a draft that somebody
   * interrupted. Having the steal path build the payload its own way would
   * mean two readers of the same form drifting apart, and the one that
   * drifted would be the one that only runs when a person gets interrupted —
   * the hardest path to notice being wrong.
   */
  function readForm(): {
    body: QueuedTransactionPayload;
    firstDebitAccountId?: string;
    firstCreditAccountId?: string;
    /** Legs whose account-currency amount is required and was left blank. */
    missingAmount: string[];
  } {
    const data = new FormData(form);
    const currencyCode = currencySelect.value;
    let firstDebitAccountId: string | undefined;
    let firstCreditAccountId: string | undefined;
    const missingAmount: string[] = [];
    // `[data-row-id]`, not every `tr`: the split table also carries the
    // "yours, held" annotation rows, which are not legs.
    const splits = [...rowsEl.querySelectorAll<HTMLTableRowElement>('tr[data-row-id]')].map((tr) => {
      const rowId = tr.dataset['rowId']!;
      const accountId = (tr.querySelector('[data-split-account]') as HTMLSelectElement).value;
      const memo = (tr.querySelector('[data-split-memo]') as HTMLInputElement).value;
      const debit = Number((tr.querySelector('[data-split-debit]') as HTMLInputElement).value) || 0;
      const credit = Number((tr.querySelector('[data-split-credit]') as HTMLInputElement).value) || 0;
      const value = debit - credit;
      if (value > 0 && !firstDebitAccountId) firstDebitAccountId = accountId;
      if (value < 0 && !firstCreditAccountId) firstCreditAccountId = accountId;
      const row = rows.find((r) => r.id === rowId)!;

      const amountInput = tr.querySelector<HTMLInputElement>('[data-split-amount]');
      const fixedCurrencyEl = tr.querySelector<HTMLElement>('[data-split-fixed-currency]');
      const splitCurrencySelect = tr.querySelector<HTMLSelectElement>('[data-split-currency]');
      const splitCurrency = fixedCurrencyEl?.dataset['splitFixedCurrency'] ?? splitCurrencySelect?.value ?? currencyCode;

      /*
       * An empty box is not a zero.
       *
       * It read `Number(input.value) || 0`, so a blank field saved the leg
       * with an account-currency amount of **0** against a value of ±3,000:
       * the transaction still balanced, the account's own balance did not
       * move, and nothing said so. Silent, and in the only column whose
       * entire job is to say what really moved.
       *
       * Blank means two different things and they are not interchangeable.
       * On a leg whose currency IS the transaction's, the number is the value
       * — there is nothing to ask for, so it is filled in. On a leg in
       * another currency, the number is the half of the exchange rate only a
       * person can supply, and the save stops rather than invent one.
       */
      const read = amountInput
        ? accountCurrencyAmount(amountInput.value, splitCurrency, currencyCode, value)
        : { amount: Math.abs(value) };
      if ('missing' in read) {
        missingAmount.push(bookableAccounts.find((a) => a.id === accountId)?.name ?? 'this leg');
      }
      const amountAbs = 'missing' in read ? 0 : read.amount;
      const amount = String(value >= 0 ? amountAbs : -amountAbs);

      // A wallet only means something on a leg that holds money, and the
      // picker is hidden on the others — so a category leg sends null rather
      // than whatever the select happened to be showing before the account
      // was changed (ADR-0023).
      const walletSelect = tr.querySelector<HTMLSelectElement>('[data-split-wallet]');
      const walletId = isMoneyAccount(accountId) && walletSelect?.value ? walletSelect.value : null;

      return row.isNew
        ? { id: rowId, accountId, memo, value: String(value), amount, currencyCode: splitCurrency, walletId }
        : { id: rowId, op: 'update', baseVersion: row.baseVersion ?? 0, accountId, memo, value: String(value), amount, currencyCode: splitCurrency, walletId };
    });
    for (const id of removedSplitIds) splits.push({ id, op: 'delete' } as never);

    return {
      body: {
        id: txId,
        txDate: formString(data, 'txDate'),
        reference: formString(data, 'reference'),
        description: formString(data, 'description'),
        currencyCode,
        splits,
        ...(existing ? { baseVersion } : {}),
        ...walletMoveBody(),
      },
      ...(firstDebitAccountId !== undefined ? { firstDebitAccountId } : {}),
      ...(firstCreditAccountId !== undefined ? { firstCreditAccountId } : {}),
      missingAmount,
    };
  }

  /**
   * The sidecar, as this form would have it.
   *
   * Nothing at all when the legs do not cross two wallets: an absent
   * `walletMove` means "this save has nothing to say about it", which is the
   * only safe thing to send about a question the form is not asking. An
   * EMPTY kind is the other thing — "there is no longer one of these" — and
   * is what "Just moving it" sends, so turning a loan back into a plain move
   * actually removes the record rather than leaving the old one standing.
   *
   * The id is only ever used when the server finds nothing to update; it
   * reconciles against what is already there (`walletMoveMembers`), which is
   * what stops repeated saves stacking sidecars on one transaction.
   */
  function walletMoveBody(): Pick<QueuedTransactionPayload, 'walletMoves'> | Record<string, never> {
    // Nothing at all when the legs do not cross two wallets: an absent list
    // means "this save has nothing to say about it", which is the only safe
    // thing to send about a question the form is not asking. An EMPTY list is
    // the other thing — "there are none of these any more" — and is what
    // removing the last row sends, so taking a loan back off an entry
    // actually removes the record rather than leaving the old one standing.
    if (moveBar.hidden) return {};
    return { walletMoves: moveRowValues() };
  }

  /** Why these moves cannot be recorded as asked, if they cannot. */
  function moveRefusal(): string | undefined {
    if (moveBar.hidden) return undefined;
    const moves = moveRowValues();
    for (const move of moves) {
      if (!move.fromWalletId || !move.toWalletId) {
        return 'Say which wallet each move came out of and which it went into, or remove the row.';
      }
      if (move.fromWalletId === move.toWalletId) {
        return 'A move between wallets needs two different wallets.';
      }
      if (!move.amountSplitId) {
        // The question this whole list exists to answer. Without it the read
        // path is back to guessing which leg the move was about, and at four
        // legs its guess matched more than one and counted the move twice.
        return 'Say which amount each move was — pick one of the entry\u2019s own lines.';
      }
      // A backstop for the picker's filter: a row loaded against legs that
      // have since changed can be holding a leg the list would not offer now.
      const chosen = moveLegChoices().find((c) => c.value === move.amountSplitId);
      if (!chosen || !legFitsMove(chosen.walletId, move.fromWalletId, move.toWalletId)) {
        return 'One of the moves points at an amount in a third wallet.'
          + ' Pick one of its two wallets\u2019 own lines, or a line in no wallet.';
      }
      if (move.amount !== null && !(Number(move.amount) > 0)) {
        return 'A part of a line has to be more than zero \u2014 or leave it empty for all of it.';
      }
    }
    // However the rows share a leg out, together they cannot claim more of it
    // than there is. An empty amount is all of it. The server holds the same
    // line (`assertTransfersFitLegs`); this says so before the round trip.
    const claimed = new Map<string, number>();
    for (const move of moves) {
      const leg = moveLegChoices().find((c) => c.value === move.amountSplitId)!;
      const part = move.amount === null ? leg.size : Number(move.amount);
      claimed.set(leg.value, (claimed.get(leg.value) ?? 0) + part);
      if (claimed.get(leg.value)! > leg.size + 0.00005) {
        return `The moves on ${leg.label} add up to more than it is.`
          + ' Lower an amount, or pick another line for one of them.';
      }
    }
    return undefined;
  }

  async function save(): Promise<void> {
    clearError(); // this attempt gets to speak for itself
    // A snapshot's splits carry no live version, so saving one could only
    // ever fail — and if it somehow did not, it would be a restore nobody
    // asked for. The Restore button is the way to turn looking into changing.
    if (timeline.viewing() !== undefined) {
      showError('This is a past version. Use “Back to current” to edit, or restore this version from the history.');
      return;
    }
    const { body: form, firstDebitAccountId, firstCreditAccountId, missingAmount } = readForm();
    /*
     * Refuse rather than guess.
     *
     * This number is one half of an exchange rate — two legs of -100.00 EUR
     * and +38,500 HUF *are* the rate, which is why nothing stores one. A
     * blank in a currency that is not the transaction's is therefore missing
     * information, not a zero, and the only honest thing to do with it is
     * ask.
     */
    if (missingAmount.length > 0) {
      showError(
        `Fill in the amount in the account's own currency for ${missingAmount.join(', ')} — `
        + 'that figure is what records the exchange rate, and there is no way to work it out from here.',
      );
      return;
    }
    /*
     * A half-answered move is refused rather than dropped.
     *
     * The server would take `kind: 'loan'` with no wallets as
     * `invalid_request` and the whole save would fail on a 400 naming
     * nothing. Said here, where the controls are.
     */
    const badMove = moveRefusal();
    if (badMove) { showError(badMove); return; }
    // The tag changes are members of this same document, so they are part of
    // what gets sent, queued offline, and held or applied as a whole.
    const payload = { ...form, tags: tagSelectionMembers(tagSelection) };
    const currencyCode = payload.currencyCode;

    /**
     * Re-acquire before giving up. The common way to arrive here without a
     * lease is entirely innocent: this tab was hidden longer than the TTL, so
     * its heartbeat paused and the lease lapsed (ADR-0003, deliberately) —
     * the record is usually still free and the draft on screen is perfectly
     * good. Retrying is safe because `baseVersion` is the real guard, not the
     * lease (invariant #2): if someone edited during the lapse, the retry
     * comes back `stale-base` instead of overwriting them.
     */
    async function attemptSave(leaseId: string | undefined): Promise<Response> {
      const body = { ...payload, ...(existing ? { leaseId } : {}) };
      return existing
        ? apiFetch(`/apps/webledger/transactions/${txId}`, { method: 'PUT', token, body })
        : apiFetch('/apps/webledger/transactions', { method: 'POST', token, body });
    }

    /**
     * Offline: queue instead of sending. Both creates and edits of existing
     * records go through the outbox (ADR-0011's `offline: 'create'` policy —
     * an edit that loses its CAS on replay becomes a pending revision rather
     * than overwriting anyone).
     *
     * Queued *before* the request is attempted, since we already know the
     * socket is down — the alternative is a fetch that hangs for the
     * browser's timeout with the user watching a dead Save button.
     */
    if (transport.state !== 'open') {
      await queueOffline();
      return;
    }

    /*
     * A hold to save under. Normally this tab has it; if it does not — the
     * form was opened offline, or somebody took it over a moment ago — it is
     * asked for now. Refused means somebody else has it: what was typed here
     * is kept beside their version (`onHoldView` → `keepDraftTakenBy`), never
     * discarded. The version check is still what stops an overwrite
     * (invariant #2).
     */
    if (existing && hold) {
      const ensured = await hold.ensure();
      if ('offline' in ensured) { await queueOffline(); return; }
      if (!ensured.ok) {
        if (ensured.view.kind === 'theirs') {
          const who = personOf(ensured.view.holder);
          showError(`${who?.name ?? 'Someone else'} has this transaction now — your changes were not saved, `
            + 'and they are kept below.');
        } else {
          showError('This transaction could not be saved — you do not have permission to edit it.');
        }
        return;
      }
    }

    let res: Response;
    try {
      res = await attemptSave(heldLeaseId());
    } catch {
      // The connection went during the request itself.
      await queueOffline();
      return;
    }

    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      // The modal stays open on this path and nothing else about the form
      // changes, so announcing alone made a failed save look precisely like a
      // successful one to anyone who could not hear it.
      showError(`Could not save: ${err.error ?? res.status}`);
      return;
    }
    // Saved: whatever the held draft was proposing has now been decided, one
    // way or the other, by the values that just went to the server.
    await releaseHeldDraft();
    // Saved on the server directly, so a refused unsent version of it is moot.
    await client.clearRefused(txId);
    rememberDefaults();
    // Files held on a new entry go now that it exists. A failure cannot keep
    // this editor open — it is an editor of a NEW entry, and saving again
    // would try to create it twice — so it is said on the page instead.
    const fileFailed = !existing && attachments.heldCount() > 0 ? await attachments.sendHeld(txId) : '';
    bsModal(modalEl)?.hide();
    if (fileFailed) {
      showPageNotice(root, `Saved \u2014 but a file was not attached. ${fileFailed} Open the entry to attach it again.`);
    }

    function rememberDefaults(): void {
      // "Remember what I did last" defaults for the next brand-new
      // transaction — device-local (see the module-level comment on these
      // keys), mirroring legacy's per-user `rememberTxDefaults`.
      prefs.set(PREF.lastTxCurrency, currencyCode);
      if (firstDebitAccountId) prefs.set(PREF.lastTxDebitAccount, firstDebitAccountId);
      if (firstCreditAccountId) prefs.set(PREF.lastTxCreditAccount, firstCreditAccountId);
    }

    /**
     * Store the whole document in the outbox and replay it on reconnect.
     *
     * A create carries no `baseVersion` — a client-minted id (invariant #8)
     * cannot conflict with anything. An update carries the version this copy
     * was based on, which is the entire safety mechanism: replayed later, it
     * either still matches (applies cleanly) or does not (held as a pending
     * revision for review). No lease is sent, because none exists offline
     * (ADR-0011) — `baseVersion` is doing the work the lease does online.
     */
    async function queueOffline(): Promise<void> {
      // Through the client, which also lays it over the local copy so the log
      // and the totals show it at once, folds an edit of a not-yet-sent entry
      // into its create, and sends it the moment the server can be reached.
      await client.queueTransaction(existing ? 'update' : 'create', payload);
      rememberDefaults();
      bsModal(modalEl)?.hide();
      // Tags ride inside `payload` as members of this same document, so they
      // queue and replay with it rather than needing a second trip.
      notify('Saved on this device. It will sync when the connection comes back.');
      // Files need the server; a queued entry is not on it yet.
      if (attachments.heldCount() > 0) {
        attachments.dropHeld();
        showPageNotice(root, 'Saved on this device, without its files: attaching needs a connection. '
          + 'Attach them to the entry once it has synced.');
      }
      onSaved(txId);
    }
  }

  // ---------------------------------------------------------- deleting

  /**
   * Delete this transaction — legacy's `tx_delete`, behind a confirmation
   * that says what actually happens.
   *
   * Three things the dialog has to state, because each of them surprises
   * someone otherwise:
   *
   *  - the legs go too, in **every** account the transaction touches, not
   *    just the one whose register you opened it from;
   *  - the delete is soft, so it can be brought back from "Deleted records" — legacy's
   *    equivalent says "cannot be undone", which here would be untrue;
   *  - a bank-imported transaction stays imported. Its feed reference is
   *    kept on purpose, so deleting it does **not** make it come back on the
   *    next sync. That is the opposite of legacy's behaviour and exactly the
   *    kind of thing to say up front rather than let someone discover by
   *    re-syncing and finding nothing.
   *
   * Online only, and the button says so rather than silently queueing: an
   * offline delete would need its own pending-revision shape (ADR-0011) and
   * this is not the change to invent one for.
   */
  deleteBtn.addEventListener('click', () => {
    if (!existing || readOnly) return;
    const what = tx?.description?.trim() ? `"${tx.description.trim()}"` : 'this transaction';
    const offline = knownOffline();
    void (async () => {
      const go = await askConfirm({
        title: `Delete ${what}?`,
        message: [
          'Its legs are removed from every account it touches, not only this one.',
          offline
            // Said differently offline, because what happens is different: it
            // goes now and travels later, and it can still be refused when it
            // gets there.
            ? 'You are offline, so this is remembered on this device and sent when you are '
              + 'back. Until then you can undo it from "Deleted records". If somebody changes '
              + 'this transaction before your delete arrives, the delete is refused and the '
              + 'transaction comes back.'
            : 'The deletion is soft: it stays under "Deleted records", where anyone who could '
              + 'delete it can put it back, with its history intact. If it came from a bank, '
              + 'it stays imported: deleting it will not make it reappear on the next sync.',
        ],
        confirm: 'Delete it', cancel: 'Keep it', tone: 'danger',
      });
      if (!go) return;
      deleteBtn.disabled = true;

      /*
       * Offline, or online and unreachable: queue it rather than lose it.
       *
       * The plan distinguishes the two cases that look identical from here —
       * a record the server has never seen (drop its queued create; there is
       * nothing to tell anyone) from one it already has (a real delete,
       * carrying the version it was decided at). See `planOfflineDelete`.
       */
      if (offline) {
        await queueDelete();
        deleteBtn.disabled = false;
        return;
      }

      const res = await apiFetch(
        `/apps/webledger/transactions/${txId}?baseVersion=${encodeURIComponent(String(tx?.version ?? ''))}`,
        { method: 'DELETE', token },
      ).catch(() => undefined);
      deleteBtn.disabled = false;

      // Unreachable is not a refusal. The server never heard the question, so
      // this is the offline case arriving by a different route, and losing
      // the user's intent here would be the one unrecoverable outcome.
      if (!res) { await queueDelete(); return; }

      if (!res.ok) {
        // In the modal, which is still open and covering the page.
        showError(await failureMessage(res, 'Could not delete this transaction.'));
        return;
      }
      // The lease goes before the modal does, or the record stays reserved by
      // a tab that is no longer showing it.
      void leaseClient.release(leaseKey).catch(() => undefined);
      bsModal(modalEl)?.hide();
      notify('Transaction deleted.');
      onSaved(txId);
    })();

    async function queueDelete(): Promise<void> {
      const plan = planOfflineDelete(await outbox.allDocuments(), txId, tx?.version ?? 0);
      if (plan.kind === 'drop-unsent-create') {
        await outbox.removeDocument(plan.outboxId);
        notify('Deleted. It had not been sent yet, so nothing goes to the server.');
      } else {
        await outbox.enqueueDocument({
          entity: 'transaction', recordId: txId, op: 'delete',
          payload: { baseVersion: plan.baseVersion },
          clock: session.nextClock(),
        });
        notify('Deleted on this device. It will be sent when you are back online.');
      }
      // Before `onSaved`, which redraws: the list has to already know this
      // record is hidden, or it reappears for one frame.
      await client.refreshPendingWork();
      void leaseClient.release(leaseKey).catch(() => undefined);
      bsModal(modalEl)?.hide();
      onSaved(txId);
    }
  });

  // ------------------------------------------------------------ tags

  /**
   * The transaction's tags, as chips, with a picker for the rest.
   *
   * A `<select>` rather than free text: a tag is a shared vocabulary, and
   * letting one be invented here would grow "Renovation", "renovation" and
   * "renovaton" into three labels that filter to three different answers.
   * New tags are made in the manager, deliberately, where their usage is
   * visible.
   */
  function renderTags(): void {
    // Shown even on a transaction that does not exist yet. It could not be,
    // while a chip wrote a row the instant it was clicked; now that the
    // change waits for save, the links are simply written afterwards against
    // the id this transaction was minted with.
    tagsBar.hidden = false;
    renderTagSelection(tagListEl, tagPickEl, allTags, tagSelection, readOnly);
  }

  async function loadTagState(): Promise<void> {
    allTags = await loadTags(token);
    if (existing) await loadTagSelection(token, txId, tagSelection);
    renderTags();
  }

  wireTagSelection(tagListEl, tagPickEl, tagSelection, () => {
    tagNoteEl.textContent = '';
    renderTags();
  });

  void loadTagState();

  // ----------------------------------------------------------- files


  // ------------------------------------------- merging two bank entries

  interface MergeLegDto {
    transactionId: string; txDate: string; description: string;
    accountName: string; splitId: string; amount: string; currencyCode: string;
    bank: string; wallet: string;
  }
  interface MergeSideDto {
    transactionId: string; txDate: string; description: string; bank: string; legs: MergeLegDto[];
  }
  interface MergeCandidateDto extends MergeSideDto { crossCurrency: boolean }

  function closeMergePanel(): void {
    mergePanel.hidden = true;
    mergePanel.innerHTML = '';
    mergeBtn.setAttribute('aria-expanded', 'false');
  }

  function moneyFor(code: string): { fraction: number; symbol?: string; code: string } {
    const currency = currencies.find((c) => c.code === code);
    return { fraction: currency?.fraction ?? 2, ...(currency?.symbol ? { symbol: currency.symbol } : {}), code };
  }

  /**
   * Does this transaction have a bank leg at all?
   *
   * One small request when an existing transaction opens, and the button
   * appears only if the answer is yes. The alternative — legacy's — is a
   * button on every transaction that answers "this has no bank leg to merge"
   * for the great majority of them, which is an affordance that teaches
   * people to ignore it. The cost is a query per editor open; it is an
   * id-filtered join and this app has five users.
   */
  function probeMergeable(): void {
    if (!existing) return;
    void (async () => {
      const res = await apiFetch(`/apps/webledger/transactions/${txId}/merge-candidates`, { token })
        .catch(() => undefined);
      if (!res?.ok) return;
      /*
       * Offered only when there is actually something to merge with.
       *
       * It used to be enough that this transaction had a bank leg, which was
       * nearly the same question back when only bank-imported entries could
       * merge at all. Now that any entry holding money can, "has a money leg"
       * is true of almost everything, and a button that answers "nothing
       * found" for the great majority is an affordance that teaches people to
       * ignore it.
       */
      const { candidates } = (await res.json().catch(() => ({ candidates: [] }))) as { candidates?: unknown[] };
      if (!candidates || candidates.length === 0) return;
      mergeable = true;
      mergeBtn.hidden = readOnly;
    })();
  }

  async function openMergePanel(): Promise<void> {
    if (!mergePanel.hidden) {
      closeMergePanel();
      return;
    }
    mergePanel.hidden = false;
    mergeBtn.setAttribute('aria-expanded', 'true');
    mergePanel.innerHTML = '<p class="text-secondary small mb-0">Looking for the other half…</p>';

    const res = await apiFetch(`/apps/webledger/transactions/${txId}/merge-candidates`, { token })
      .catch(() => undefined);
    if (!res?.ok) {
      const body = res ? (await res.json().catch(() => ({}))) as { message?: string } : {};
      mergePanel.innerHTML = `<p class="text-secondary small mb-0">${escapeHtml(
        body.message ?? 'Could not look for the other half — check your connection.')}</p>`;
      return;
    }
    const { self, candidates } = (await res.json()) as { self: MergeSideDto; candidates: MergeCandidateDto[] };
    if (candidates.length === 0) {
      mergePanel.innerHTML = '<p class="text-secondary small mb-0">'
        + `Nothing moving the other way within ten days of ${escapeHtml(self.txDate)}.</p>`;
      return;
    }

    /*
     * One leg, as a line you can choose.
     *
     * The wallet is on it because it is often the only thing that tells two
     * otherwise identical legs apart — a 3,000 out of one account and a 3,000
     * into another, on the same day, differ by their earmark and nothing
     * else.
     */
    const legLine = (leg: MergeLegDto): string =>
      `<span class="d-flex justify-content-between gap-2">
        <span>${escapeHtml(leg.accountName)}</span>
        <span class="text-nowrap">${escapeHtml(formatAmount(leg.amount, moneyFor(leg.currencyCode)))}</span>
      </span>`
      + (leg.wallet ? `<span class="d-block text-secondary">${escapeHtml(leg.wallet)}</span>` : '');

    /*
     * Pick a leg, or be told which one it will be.
     *
     * A side with one money leg has no question in it, so it states the
     * answer instead of asking — radio buttons for a choice of one are a
     * decision theatre. A side with several asks, with the first preselected
     * so the common case is still one click.
     */
    const legChooser = (side: MergeSideDto, name: string): string =>
      (side.legs.length === 1
        ? `<div class="small text-secondary px-2 py-1">${legLine(side.legs[0]!)}</div>`
        : side.legs.map((leg, i) => `
            <label class="list-group-item py-1 small d-flex gap-2 align-items-start">
              <input class="form-check-input mt-1 flex-shrink-0" type="radio" name="${escapeHtml(name)}"
                value="${escapeHtml(leg.splitId)}" ${i === 0 ? 'checked' : ''}>
              <span class="flex-grow-1">${legLine(leg)}</span>
            </label>`).join(''));

    mergePanel.innerHTML = `
      <div class="cf-h mb-1">Merge with the other half</div>
      <p class="text-secondary small mb-2">
        One movement of money that got written down twice. Picking an entry below keeps
        <em>this</em> transaction, brings that one's leg onto it, drops the legs that were
        standing in for the missing half, and balances the two against each other — which is also
        what puts an exchange rate on the record when the currencies differ.
      </p>
      <div class="text-secondary small mb-1">Keep from this one, on ${escapeHtml(self.txDate)}:</div>
      <div class="list-group mb-2" data-merge-self>${legChooser(self, 'mergeSelfLeg')}</div>
      <div class="text-secondary small mb-1">And bring over from:</div>
      <div class="list-group">
        ${candidates.map((c) => `
          <div class="list-group-item p-0">
            <!-- Two deliberate lines rather than one flexed row: on a phone
                 the date and the amount would otherwise end up sharing a line
                 with half the account name trailing underneath, which reads
                 as a mistake. The identity goes on top, the detail below. -->
            <div class="d-flex justify-content-between gap-2 px-2 pt-1 small">
              <span class="fw-semibold text-nowrap">${escapeHtml(c.txDate)}</span>
              ${c.crossCurrency ? '<span class="badge text-bg-info">exchange</span>' : ''}
            </div>
            <div class="px-2 text-secondary small">${escapeHtml(c.description)}${
              c.bank ? ` \u00b7 ${escapeHtml(c.bank)}` : ''}</div>
            <div class="list-group list-group-flush">${legChooser(c, `mergeLeg-${c.transactionId}`)}</div>
            <div class="px-2 pb-1">
              <button type="button" class="btn btn-sm btn-outline-primary w-100"
                data-merge-pick="${escapeHtml(c.transactionId)}">Merge with this one</button>
            </div>
          </div>`).join('')}
      </div>`;

    const chosenLeg = (scope: ParentNode, name: string, side: MergeSideDto): string =>
      (side.legs.length === 1
        ? side.legs[0]!.splitId
        : scope.querySelector<HTMLInputElement>(`input[name="${CSS.escape(name)}"]:checked`)?.value ?? '');

    for (const button of mergePanel.querySelectorAll<HTMLButtonElement>('[data-merge-pick]')) {
      const otherId = button.dataset['mergePick']!;
      const side = candidates.find((c) => c.transactionId === otherId)!;
      button.addEventListener('click', () => void doMerge(otherId, {
        keepSplitId: chosenLeg(mergePanel, 'mergeSelfLeg', self),
        otherSplitId: chosenLeg(button.closest('.list-group-item')!, `mergeLeg-${otherId}`, side),
      }));
    }
  }

  async function doMerge(otherId: string, picks: { keepSplitId: string; otherSplitId: string }): Promise<void> {
    const go = await askConfirm({
      title: 'Merge these two into one transaction?',
      message: [
        'The other one is removed. The leg you chose comes here along with its earmark, its '
          + 'description and any bank references, and the two legs are balanced against each other.',
        'The legs that were standing in for the missing half are dropped. Anything that came from '
          + 'a bank stays de-duplicated, so it can never be imported again.',
      ],
      confirm: 'Merge them', cancel: 'Leave them apart', tone: 'danger',
    });
    if (!go) return;

    const res = await apiFetch(`/apps/webledger/transactions/${txId}/merge`, {
      method: 'POST', token, body: { otherId, ...picks },
    }).catch(() => undefined);
    if (!res?.ok) {
      const body = res ? (await res.json().catch(() => ({}))) as { message?: string } : {};
      const message = body.message ?? 'Could not reach the server to merge.';
      // Into the panel, not only into `notify`: that writes to the shared
      // `.cf-sr` region, which is visually hidden, so a refused merge would
      // look exactly like a button that did nothing.
      const failure = document.createElement('div');
      failure.className = 'alert alert-danger py-2 small mt-2 mb-0';
      failure.textContent = message;
      mergePanel.querySelector('.alert-danger')?.remove();
      mergePanel.append(failure);
      notify(message);
      return;
    }
    closeMergePanel();
    notify('Merged. The exchange rate is now recorded as the two legs of this transaction.');
    // `onSaved` fires from the modal's own hidden handler, so the list
    // reloads and lands on the surviving transaction.
    bsModal(modalEl)?.hide();
  }

  const mergeClickHandler = (): void => { void openMergePanel(); };
  mergeBtn.addEventListener('click', mergeClickHandler);
  probeMergeable();

  /**
   * The form is movable too. Between the modal and the history panel there is
   * always something covering something, and rather than search for a layout
   * that never collides, either one can be pushed aside.
   */
  const dialogEl = modalEl.querySelector<HTMLElement>('.modal-dialog');
  const dialogHandle = modalEl.querySelector<HTMLElement>('.modal-header');
  const modalDrag = dialogEl && dialogHandle
    ? makeDraggable({ panel: dialogEl, handle: dialogHandle })
    : undefined;

  /*
   * The baseline, taken once the form is built and before it is shown.
   *
   * Where it is declared is far too early: the split rows, the tags and the
   * currency list are all filled after that point, and a baseline captured
   * against an empty table makes every form dirty the moment it finishes
   * drawing itself.
   */
  openedWith = formSnapshot(form);

  // After the snapshot, so a restored draft reads as what it is: unsaved.
  if (opts.restore) await applyRestoredDraft(opts.restore);

  /*
   * Unsaved changes are asked about before the page is left — closing a tab
   * used to lose them silently — and kept in the tab as they are typed, so a
   * tab the browser discards comes back with them (ADR-0003, 2026-10-22).
   */
  const unguard = guardUnsaved(hasUnsavedChanges);
  let rememberTimer: ReturnType<typeof setTimeout> | undefined;
  const rememberSoon = (): void => {
    if (rememberTimer !== undefined) clearTimeout(rememberTimer);
    rememberTimer = setTimeout(() => {
      if (!hasUnsavedChanges()) { forgetForm(); return; }
      rememberForm({
        form: 'full', txId: existing ? txId : null,
        payload: { ...readForm().body, tags: tagSelectionMembers(tagSelection) },
      });
    }, 400);
  };
  form.addEventListener('input', rememberSoon);
  form.addEventListener('change', rememberSoon);

  formReady = true;
  if (freedBeforeReady) void pickUpFreed();

  bsModal(modalEl)?.show();

  const onHidden = (): void => {
    prevBtn.removeEventListener('click', prevHandler);
    nextBtn.removeEventListener('click', nextHandler);
    unswipe();
    form.removeEventListener('submit', submitHandler);
    root.querySelector('#txAddSplit')?.removeEventListener('click', addSplitHandler);
    currencySelect.removeEventListener('change', currencyPickedHandler);
    dateInput.removeEventListener('input', dateInputHandler);
    referenceInput.removeEventListener('input', referenceInputHandler);
    descriptionInput.removeEventListener('input', descriptionInputHandler);
    modalEl.removeEventListener('hidden.bs.modal', onHidden);
    modalEl.removeEventListener('click', modalClickHandler);
    mergeBtn.removeEventListener('click', mergeClickHandler);
    mergeBtn.hidden = true;
    closeMergePanel();
    // Back to centre for whoever opens it next — where a panel sits is a
    // property of the moment, not a setting.
    modalDrag?.reset();
    modalDrag?.dispose();
    // Its listeners are on the document, so a modal opened and closed twenty
    // times would otherwise leave twenty calendars listening.
    dateField.dispose();
    // And the files bar's: its listeners sit on the modal's own input and
    // list, which outlive this editor. Never taken down before, so every
    // editor ever opened went on listening — a file chosen later could be
    // sent to an entry that was open earlier. Also lets held files go.
    attachments.dispose();
    timeline.close(false); // the form is going away; nothing to put back
    historyBtn.hidden = true;
    unsubscribeTransport?.();
    unsubscribePresence?.();
    unsubscribeReconnect?.();
    hold?.dispose();
    // Closed on purpose: nothing to bring back, and nothing left to ask about.
    unguard();
    if (rememberTimer !== undefined) clearTimeout(rememberTimer);
    form.removeEventListener('input', rememberSoon);
    form.removeEventListener('change', rememberSoon);
    forgetForm();
    // Releases the lease (if still held for this key) and leaves presence —
    // one call covers both, see the matching `openRecord` call above.
    if (existing) session.closeRecord('transaction', txId);
    onSaved(txId);
  };
  modalEl.addEventListener('hidden.bs.modal', onHidden);
}

/**
 * Reopen the full editor a discarded tab had open on this page, with its
 * unsaved values (webledgerFormRestore.ts). For pages whose only form is this
 * one; the wallets page also restores its short form, and does it itself.
 * True when there was one to reopen.
 */
export async function reopenDiscardedEditor(
  root: HTMLElement, client: WebledgerClient, accounts: AccountDto[], currencies: CurrencyDto[],
  onSaved: (txId: string) => void,
): Promise<boolean> {
  const saved = takeSavedForm();
  if (!saved || saved.form !== 'full') return false;
  let existing: TransactionDto | null = null;
  if (saved.txId) {
    const res = await apiFetch(`/apps/webledger/transactions/${saved.txId}`, { token: client.token }).catch(() => undefined);
    existing = res?.ok ? ((await res.json()) as { transaction: TransactionDto }).transaction : queuedPayloadToDto(saved.payload);
  }
  await openTransactionEditor(root, client, existing, accounts, currencies, onSaved, { restore: saved.payload });
  return true;
}
